// Structured copy and data for CPH Collective in both Danish and English

export const SERVICES_DATA = [
  {
    id: "turnkey",
    slug: {
      da: "totalentreprise",
      en: "turnkey"
    },
    title: {
      da: "Totalentreprise",
      en: "Turnkey Services"
    },
    subtitle: {
      da: "Komplette projektløsninger med ét enkelt kontaktpunkt",
      en: "Complete project solutions with single-point accountability"
    },
    seoTitle: {
      da: "Totalentreprise København | CPH Collective",
      en: "Turnkey Solutions Copenhagen | CPH Collective"
    },
    seoDescription: {
      da: "Totalentreprise i København og på Sjælland. Samlet styring af projektering, tømrerarbejde og udførelse hos én ansvarlig partner.",
      en: "Turnkey construction in Copenhagen and Zealand. Unified management of engineering, carpentry, and execution under one partner."
    },
    seoKeywords: {
      da: [
        "totalentreprise København",
        "totalentreprise Sjælland",
        "byggefirma",
        "projektstyring byggeri",
        "erhvervsbyggeri entreprenør",
        "hovedentreprise",
        "byggeledelse"
      ],
      en: [
        "turnkey services Copenhagen",
        "turnkey services Zealand",
        "full-service construction company",
        "project management construction",
        "contractor for commercial buildings",
        "general contracting",
        "construction supervision"
      ]
    },
    description: {
      da: "Med totalentreprise overtager vi det fulde ansvar for hele byggeprojektet – fra planlægning til færdiggørelse. Vi fungerer som dit eneste kontaktpunkt og koordinerer alle involverede faggrupper.",
      en: "With turnkey services, we take full responsibility for the entire construction project—from planning to completion. We act as a single point of contact and coordinate all involved trades."
    },
    longDescription: {
      da: [
        "Ved en totalentreprise hos CPH Collective samles projektering, planlægning, faggrupper og udførelse hos én ansvarlig partner. Det giver jer færre grænseflader, reduceret risiko og et klarere overblik over beslutninger, tidsplan og økonomi. Løsningen passer især til byggeri med mange aktører og høje krav til koordinering.",
        "Vi begynder altid med at afklare behov, omfang og forventninger i tæt dialog med bygherre. Derefter kan vi håndtere projektering af byggeri, koordinere underentreprenører og følge op på kvalitet og fremdrift. Vores projektarbejde er 100% gennemskueligt, og I får løbende afrapportering, så vigtige valg træffes på et oplyst grundlag.",
        "Uanset om der er tale om nybyggeri til erhverv, gennemgribende ombygning eller komplekse renoveringer, sikrer vores strukturerede projektledelse, at byggeriet afleveres til den aftalte tid og i den forventede høje håndværksmæssige standard."
      ],
      en: [
        "With a turnkey contract with CPH Collective, engineering, planning, trades, and execution are united under one accountable partner. This provides fewer interfaces, reduced risk, and clearer oversight of decisions, schedule, and budget. The solution is ideal for projects with many stakeholders and high coordination requirements.",
        "We always start by clarifying needs, scope, and expectations in close dialogue with the client. We then manage building engineering, coordinate subcontractors, and supervise quality and progress. Our project work is completely transparent, providing continuous reporting so key decisions are made on an informed foundation.",
        "Whether commercial new builds, thorough conversions, or complex renovations, our structured project management ensures that the construction is handed over on time and to the highest craftsmanship standards."
      ]
    },
    includes: {
      da: [
        "Totalansvar for projektering og planlægning",
        "Koordinering af alle faggrupper og underentreprenører",
        "Stram tidsplanlægning og budgetstyring",
        "Løbende myndighedsdialog og byggetilladelser",
        "Uafhængig kvalitetssikring (KS) og tilsyn",
        "Løbende kommunikation og afrapportering til bygherre"
      ],
      en: [
        "Single-point responsibility for engineering and planning",
        "Coordination of all trades and subcontractors",
        "Strict schedule tracking and budget management",
        "Authority handling and building permits",
        "Independent quality assurance (QA) and on-site supervision",
        "Continuous transparent reporting to the client"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Behovsafklaring & Budget", en: "Scope & Budget Clarification" },
        text: {
          da: "Vi gennemgår projektets forudsætninger, bygherres ønsker, tidsramme og økonomiske rammer for at skabe et realistisk grundlag.",
          en: "We review project parameters, client objectives, timelines, and financial limits to establish a realistic baseline."
        }
      },
      {
        step: "02",
        title: { da: "Projektering & Udbud", en: "Engineering & Procurement" },
        text: {
          da: "Udarbejdelse af detaljerede projekttegninger, tidsplaner og indhentning af priser fra udvalgte, pålidelige underentreprenører.",
          en: "Drafting detailed project plans, schedules, and procuring pricing from verified, trusted subcontractors."
        }
      },
      {
        step: "03",
        title: { da: "Styret Byggeproces", en: "Controlled Construction" },
        text: {
          da: "Vores byggeledelse er fast til stede på pladsen, følger fremdriften minutiøst og håndterer alle grænseflader mellem faggrupperne.",
          en: "Our site management maintains daily presence, rigorously tracking progress and managing inter-trade interfaces."
        }
      },
      {
        step: "04",
        title: { da: "Aflevering & 1-års Gennemgang", en: "Handover & Warranty Follow-up" },
        text: {
          da: "Grundig afleveringsforretning med komplet dokumentation, drift- og vedligeholdelsesmanual samt opfølgning efter idriftsættelse.",
          en: "Thorough handover inspection with full documentation, operation manuals, and post-occupancy warranty review."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Én ansvarlig aftalepart", en: "Single Accountable Partner" },
        text: { da: "I slipper for at mægle mellem arkitekter, ingeniører og faggrupper. Vi bærer det samlede ansvar.", en: "No mediating between designers and trades. We carry full accountability." }
      },
      {
        title: { da: "Præcis budgetoverholdelse", en: "Accurate Budget Control" },
        text: { da: "Gennemskuelige aftaler og proaktiv risikostyring minimerer uforudsete ekstraomkostninger.", en: "Transparent agreements and proactive risk management minimize unforeseen extra costs." }
      },
      {
        title: { da: "Hurtigere gennemførelse", en: "Faster Execution" },
        text: { da: "Overlappende projektering og indkøb reducerer den samlede byggetid markant.", en: "Overlapping engineering and procurement significantly reduces overall build time." }
      }
    ],
    idealFor: {
      da: "Totalentreprise er ideelt for bygherrer, ejendomsudviklere og erhvervskunder, der ønsker tryghed, overblik og professionelt styret projektudførelse.",
      en: "Turnkey services are ideal for clients, developers, and corporate entities seeking peace of mind, oversight, and professionally managed project execution."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-erhvervsbyggeri-kontor-facade.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-arkitektur-totalentreprise-koebenhavn.jpg",
    faq: [
      {
        q: {
          da: "Hvad er forskellen på en totalentreprise og en hovedentreprise?",
          en: "What is the difference between a turnkey contract and a general contract?"
        },
        a: {
          da: "Ved en totalentreprise overtager CPH Collective ansvaret for både projektering, tegninger, myndighedsansøgninger og den praktiske udførelse på byggepladsen. I en hovedentreprise står bygherre derimod selv for projekteringen med egne arkitekter og ingeniører, hvorefter entreprenøren udelukkende bygger efter de udleverede tegninger. Totalentreprisen minimerer bygherres risiko for fejl i grænsefladerne mellem projektering og udførelse, da ansvaret er samlet ét sted. Det sikrer en mere smidig byggeproces med færre misforståelser og en entydig ansvarsfordeling.",
          en: "In a turnkey contract, CPH Collective assumes full responsibility for architectural planning, engineering, authority permits, and physical execution on the jobsite. Under a general contract, by contrast, the client oversees design and engineering independently, after which the contractor builds strictly from the provided specifications. A turnkey approach minimizes the client's exposure to interface conflicts between design and craftsmanship because accountability is consolidated. This ensures an aligned construction sequence with clear lines of responsibility."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I, at budgettet overholdes under en totalentreprise?",
          en: "How do you ensure budget compliance during a turnkey construction project?"
        },
        a: {
          da: "Vi etablerer fra start et detaljeret kalkulationsgrundlag, hvor alle faser, materialer og underleverandører er nøje prissat. Gennem hele byggeforløbet anvender vi en åben og transparent budgetstyring med faste milepæle og løbende økonomirapportering. Eventuelle uforudsete forhold eller ændringsønsker belyses altid skriftligt med konsekvenser for pris og tid, før de igangsættes. På den måde undgår bygherre ubehagelige økonomiske overraskelser ved aflevering af byggeriet.",
          en: "From the outset, we establish a transparent cost breakdown where all project stages, specified materials, and specialist trades are itemized. Throughout the build, we utilize open-book financial governance with scheduled milestones and regular fiscal progress reports. Any unforeseen site conditions or design changes are evaluated in writing with explicit cost and time impacts before execution begins. This systematic protocol protects clients from unexpected budgetary discrepancies upon final handover."
        }
      },
      {
        q: {
          da: "Hvem har den daglige kontakt til bygherre under projektet?",
          en: "Who handles the daily dialogue with the client during the project?"
        },
        a: {
          da: "I får tildelt en fast projektleder fra CPH Collective, der fungerer som jeres primære kontaktperson gennem hele forløbet. Projektlederen varetager al koordinering med myndigheder, underentreprenører og rådgivere, så I aldrig skal mægle mellem forskellige parter. Vi afholder faste byggemøder og sender skriftlige statusrapporter, så I altid har fuldt overblik over fremdriften.",
          en: "You are assigned a dedicated project manager from CPH Collective who serves as your direct point of contact throughout the entire engagement. Your project manager orchestrates all coordination between municipal authorities, subcontractors, and technical consultants so you never mediate between disparate parties. We convene regular site meetings and provide written progress updates, ensuring complete operational transparency at every stage."
        }
      },
      {
        q: {
          da: "Hvilke typer byggerier egner sig bedst til totalentreprise?",
          en: "Which types of construction are best suited for turnkey delivery?"
        },
        a: {
          da: "Totalentreprise er særligt fordelagtig ved erhvervsombygninger, kontorindretninger, omfattende renoveringer og større tilbygninger, hvor mange faggrupper skal spille sammen. Løsningen er også ideel for bygherrer, der ønsker at frigøre tid og ressourcer frem for at bruge energi på daglig byggepladskoordinering. Ved komplekse projekter med stramme deadlines sikrer totalentreprisen, at planlægning og udførelse kan overlappe optimalt. Vi vurderer altid opgavens karakter og rådgiver om den mest hensigtsmæssige entrepriseform.",
          en: "Turnkey delivery is particularly advantageous for commercial refurbishments, corporate fit-outs, comprehensive renovations, and substantial extensions involving multiple coordinated trades. The solution is also optimal for property owners who prioritize focus on their core business rather than micromanaging daily jobsite logistics. On complex projects with tight deadlines, turnkey management allows engineering and procurement to overlap seamlessly. We always evaluate your project parameters to recommend the most efficient procurement structure."
        }
      },
      {
        q: {
          da: "Hvordan håndterer I kvalitetssikring og aflevering?",
          en: "How do you handle quality assurance and project handover?"
        },
        a: {
          da: "Kvalitetssikring er en integreret del af vores daglige arbejde, hvor vi løbende udfører fotodokumentation og kontrolskemaer for alle fagentrepriser. Inden den officielle overdragelse gennemgår vi byggeriet internt for at identificere og udbedre eventuelle mangler. Ved selve afleveringsforretningen udarbejdes en formel protokol sammen med bygherre samt fuld D&V-dokumentation. Desuden indkalder vi automatisk til en 1-års gennemgang for at sikre, at alle løsninger fungerer upåklageligt på sigt.",
          en: "Quality assurance is built directly into our daily site routines through structured inspection protocols and detailed photographic records across all trades. Prior to the formal client handover, we conduct exhaustive pre-inspections to remedy any minor defects. At the handover milestone, a comprehensive walkthrough report is established alongside complete operation and maintenance documentation. Furthermore, we schedule a 1-year warranty review to guarantee that all architectural installations perform flawlessly over time."
        }
      }
    ]
  },
  {
    id: "engineering",
    slug: {
      da: "projektering-af-byggeri",
      en: "building-engineering"
    },
    title: {
      da: "Projektering af Byggeri",
      en: "Building Engineering & Planning"
    },
    heroHeading: {
      da: "Projektering af byggeri med styr på hele byggeprojektet",
      en: "Building engineering with control over the entire construction project"
    },
    subtitle: {
      da: "Projektering af byggeri med styr på hele byggeprojektet",
      en: "Building engineering with control over the entire construction project"
    },
    seoTitle: {
      da: "Projektering af Byggeri | CPH Collective",
      en: "Building Engineering Copenhagen | CPH Collective"
    },
    seoDescription: {
      da: "Projektering af byggeri i København. CPH Collective forener rådgivning, planlægning, projektstyring og praktisk håndværk på Sjælland.",
      en: "Building engineering and planning in Copenhagen. CPH Collective combines project management with practical craftsmanship across Denmark."
    },
    seoKeywords: {
      da: [
        "projektering af byggeri",
        "planlægning af byggeprojektet",
        "afklaring før tegninger og udførelse",
        "projektstyring",
        "projektledelse",
        "byggeprojekt",
        "byggerådgivning København",
        "byggetilladelse",
        "projekteringsledelse",
        "detailprojektering",
        "teknisk rådgivning byggeri",
        "byggefirma København",
        "byggefirma Sjælland"
      ],
      en: [
        "building engineering",
        "construction planning Copenhagen",
        "building permit handling",
        "design management",
        "detailed design construction",
        "technical building advice",
        "project management construction",
        "Copenhagen",
        "Zealand"
      ]
    },
    description: {
      da: "Et godt byggeprojekt begynder med klare valg, realistiske rammer og tydeligt ansvar. Hos CPH Collective hjælper vi bygherrer og erhvervskunder med projektering af byggeri, så planlægning, koordinering og praktisk udførelse hænger sammen fra de første afklaringer.",
      en: "A successful construction project begins with clear choices, realistic parameters, and distinct accountability. At CPH Collective, we assist clients and commercial developers with building engineering so planning, coordination, and practical execution align from the initial steps."
    },
    longDescription: {
      da: [
        "Et godt byggeprojekt begynder med klare valg, realistiske rammer og tydeligt ansvar. Hos CPH Collective hjælper vi bygherrer og erhvervskunder med projektering af byggeri, så planlægning, koordinering og praktisk udførelse hænger sammen fra de første afklaringer.",
        "Planlægning af byggeprojektet handler om mere end en tidsplan. Opgavens formål, omfang, økonomiske ramme, faglige krav og rækkefølge skal være tydelige, før udførelsen går i gang. Det giver alle parter et fælles grundlag og gør beslutninger lettere at følge."
      ],
      en: [
        "A successful construction project begins with clear choices, realistic parameters, and distinct accountability. At CPH Collective, we assist clients and commercial developers with building engineering so planning, coordination, and practical execution align from the initial steps.",
        "Planning a construction project is about far more than a timeline. The purpose, scope, budget framework, trade demands, and sequencing must be clear before execution commences. This establishes a common foundation and makes decision pathways easier to trace."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "planning-foundation",
          type: "intro-callout",
          tag: {
            da: "01 / Planlægning & Grundlag",
            en: "01 / Planning & Baseline"
          },
          title: {
            da: "Planlægning af byggeprojektet skaber et stærkere grundlag",
            en: "Planning the construction project creates a stronger foundation"
          },
          paragraphs: [
            {
              da: "Planlægning af byggeprojektet handler om mere end en tidsplan. Opgavens formål, omfang, økonomiske ramme, faglige krav og rækkefølge skal være tydelige, før udførelsen går i gang. Det giver alle parter et fælles grundlag og gør beslutninger lettere at følge.",
              en: "Planning a construction project is about more than just a schedule. The assignment's purpose, scope, economic boundaries, technical standards, and sequence must be clear before execution begins. This provides all parties with a shared foundation and makes decisions easier to navigate."
            },
            {
              da: "Vi går ind i processen med både blik for projektledelse og forståelse for selve håndværket. Det betyder, at valg kan vurderes i forhold til udførelsen på byggepladsen. Kræver projektet særskilte beregninger eller rådgiverdiscipliner, bliver de relevante fagpersoner og roller afklaret.",
              en: "We approach the process with a focus on project management combined with practical knowledge of the trade. This ensures design choices are evaluated against real-world buildability on the jobsite. Should the project require specialized structural calculations or design disciplines, the right professionals and responsibilities are promptly established."
            }
          ],
          calloutBox: {
            tag: {
              da: "Faglig Forankring & Bygbarhed",
              en: "Constructability & Technical Foundation"
            },
            text: {
              da: "Valg i projekteringen vurderes altid i forhold til den konkrete udførelse på byggepladsen – så løsninger fungerer i virkeligheden og ikke kun på papiret.",
              en: "Engineering decisions are continuously tested against practical jobsite realities—ensuring solutions work in practice, not merely on paper."
            }
          },
          stats: [
            {
              label: { da: "Fokus", en: "Focus" },
              value: { da: "Projektledelse & Håndværk", en: "Management & Craft" }
            },
            {
              label: { da: "Snitflader", en: "Interfaces" },
              value: { da: "Klare roller & ansvar", en: "Defined responsibilities" }
            },
            {
              label: { da: "Beslutninger", en: "Decisions" },
              value: { da: "Gennemtænkt før start", en: "Aligned before start" }
            }
          ]
        },
        {
          id: "clarification-before-drawings",
          type: "feature-grid",
          tag: {
            da: "02 / Forundersøgelse & Afklaring",
            en: "02 / Scoping & Clarification"
          },
          title: {
            da: "Afklaring før tegninger og udførelse",
            en: "Clarification before drawings and site execution"
          },
          paragraphs: [
            {
              da: "Før et projekt bliver til byggeri, samler vi behov, krav og kendte grænseflader. Vi ser på, hvilke fag der skal inddrages, hvad der skal besluttes hvornår, og hvordan materialer, adgangsforhold og eksisterende konstruktioner påvirker forløbet. Målet er et brugbart grundlag for næste fase.",
              en: "Before a project enters the construction phase, we consolidate requirements, specifications, and known interfaces. We evaluate which trades need involvement, what needs to be determined when, and how materials, site access, and existing structural elements affect progression. The goal is an actionable foundation for the subsequent phase."
            }
          ],
          cards: [
            {
              number: "01",
              tag: { da: "Behov & Rammer", en: "Needs & Scope" },
              title: { da: "Behov og omfang", en: "Needs and scope" },
              description: {
                da: "Vi afklarer funktion, leverancer og de krav, der har betydning for den valgte løsning.",
                en: "We define core functionality, deliverables, and essential requirements governing the chosen solution."
              },
              bullet: {
                da: "Funktion og faste krav klarlægges",
                en: "Core requirements established"
              }
            },
            {
              number: "02",
              tag: { da: "Organisation & Snitflader", en: "Roles & Accountability" },
              title: { da: "Roller og ansvar", en: "Roles and responsibilities" },
              description: {
                da: "Vi skaber overblik over bygherre, rådgivere, udførende fag og de aftalte beslutningsveje.",
                en: "We map out clients, design consultants, contracting trades, and agreed decision paths."
              },
              bullet: {
                da: "Klare beslutningsveje for alle parter",
                en: "Clear decision paths for all parties"
              }
            },
            {
              number: "03",
              tag: { da: "Faser & Rækkefølge", en: "Timeline & Sequencing" },
              title: { da: "Tid og rækkefølge", en: "Timeline and sequencing" },
              description: {
                da: "Vi planlægger milepæle og afhængigheder, så faggrupper kan arbejde i en logisk orden.",
                en: "We coordinate milestones and dependencies so trades execute in an orderly, logical rhythm."
              },
              bullet: {
                da: "Logisk arbejdsorden på byggepladsen",
                en: "Logical execution order on site"
              }
            }
          ]
        },
        {
          id: "site-management-and-coordination",
          type: "dual-cards",
          tag: {
            da: "03 / Styring & Udførelse",
            en: "03 / Supervision & Execution"
          },
          title: {
            da: "Projektstyring og projektledelse i praksis",
            en: "Project supervision and leadership in practice"
          },
          cards: [
            {
              level: "h3",
              tag: { da: "Fase 01 · Plads & Opfølgning", en: "Phase 01 · Site Monitoring" },
              title: {
                da: "Projektstyring følger arbejdet på byggepladsen",
                en: "Project supervision follows jobsite progress"
              },
              intro: {
                da: "Når udførelsen begynder, fortsætter projektstyringen med opfølgning på tidsplan, aftaler, økonomi og kvalitet. Vi koordinerer faggrupper og underentreprenører og holder den løbende dialog med bygherren. På den måde bliver ændringer og udfordringer håndteret i den rette sammenhæng.",
                en: "Once site work commences, project supervision continues with strict monitoring of schedules, agreements, finances, and craftsmanship. We coordinate trade crews and subcontractors while keeping transparent dialogue with the client, ensuring issues and modifications are resolved in proper context."
              },
              paragraphs: [
                {
                  da: "Når udførelsen begynder, fortsætter projektstyringen med opfølgning på tidsplan, aftaler, økonomi og kvalitet. Vi koordinerer faggrupper og underentreprenører og holder den løbende dialog med bygherren. På den måde bliver ændringer og udfordringer håndteret i den rette sammenhæng.",
                  en: "Once site work commences, project supervision continues with strict monitoring of schedules, agreements, finances, and craftsmanship. We coordinate trade crews and subcontractors while keeping transparent dialogue with the client, ensuring issues and modifications are resolved in proper context."
                }
              ],
              bullets: {
                da: [
                  "Opfølgning på tidsplan, aftaler, økonomi og kvalitet",
                  "Koordinering af faggrupper og underentreprenører",
                  "Løbende dialog og afstemning med bygherren",
                  "Hurtig håndtering af ændringer og udfordringer på pladsen"
                ],
                en: [
                  "Systematic monitoring of schedules, agreements, budget, and quality",
                  "Coordination of trade crews and active subcontractors",
                  "Continuous dialogue and alignment with the client",
                  "Prompt resolution of unexpected site conditions and variations"
                ]
              }
            },
            {
              level: "h3",
              tag: { da: "Fase 02 · Forbindelse til Udførelse", en: "Phase 02 · Practical Integration" },
              title: {
                da: "Projektledelse forbinder beslutninger med praktisk udførelse",
                en: "Project leadership bridges decisions with hands-on craft"
              },
              intro: {
                type: "rich",
                da: "Projektledelse skaber forbindelsen mellem de overordnede beslutninger og arbejdet på pladsen. For os er det vigtigt, at information ikke stopper i et referat, men bliver omsat til klare opgaver. Det er særligt værdifuldt i erhvervsbyggeri, {link:renovering} og projekter med mange samtidige fag.",
                en: "Project leadership connects high-level design decisions directly to jobsite activity. We ensure vital information doesn't remain trapped in meeting minutes but translates into tangible work packages. This is crucial in commercial building, {link:renovering}, and multi-trade environments."
              },
              paragraphs: [
                {
                  type: "rich",
                  da: "Projektledelse skaber forbindelsen mellem de overordnede beslutninger og arbejdet på pladsen. For os er det vigtigt, at information ikke stopper i et referat, men bliver omsat til klare opgaver. Det er særligt værdifuldt i erhvervsbyggeri, {link:renovering} og projekter med mange samtidige fag.",
                  en: "Project leadership connects high-level design decisions directly to jobsite activity. We ensure vital information doesn't remain trapped in meeting minutes but translates into tangible work packages. This is crucial in commercial building, {link:renovering}, and multi-trade environments."
                },
                {
                  type: "rich",
                  da: "CPH Collective kan både koordinere et samlet forløb og levere konkret {link:toemrerarbejde}, snedkerarbejde og montage. Ved en {link:totalentreprise} kan vi være ét kontaktpunkt fra planlægning til færdiggørelse. I andre projektformer indgår vi i den rolle, som opgaven kræver.",
                  en: "CPH Collective can coordinate the entire progression or deliver specialized {link:toemrerarbejde}, custom joinery, and mounting. Under a {link:totalentreprise}, we act as your single point of contact from planning to final handover. In other project structures, we adopt whichever role the scope demands."
                }
              ],
              bullets: {
                da: [
                  "Samlet kommunikation: I får klare kontaktveje og løbende information om beslutninger, fremdrift og ændringer.",
                  "Praktisk indsigt: Planen bliver vurderet ud fra forholdene på pladsen og den konkrete håndværksmæssige udførelse.",
                  "Løbende opfølgning: Aftaler, tidsplan og kvalitet følges gennem de relevante faser af jeres byggeprojekt."
                ],
                en: [
                  "Unified communication: You gain direct contact paths and continuous updates on decisions, progress, and changes.",
                  "Practical insight: Plans are tested against site reality and meticulous trade execution.",
                  "Continuous follow-up: Agreements, schedule, and quality are tracked throughout all key phases of your project."
                ]
              }
            }
          ]
        },
        {
          id: "geographic-coverage-engineering",
          type: "nationwide-coverage",
          tag: {
            da: "04 / Geografi & Kapacitet",
            en: "04 / Coverage & Capacity"
          },
          title: {
            da: "Projektering af byggeri i København og på Sjælland",
            en: "Building engineering in Copenhagen and across Zealand"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Vi har base i København og løser projektering af byggeri og koordinerende projektarbejde for kunder i hovedstaden og på resten af Sjælland. Vi arbejder også i Nordsjælland, hvor erhvervsbyggeri, {link:renovering} og specialopgaver ofte kræver tæt koordinering mellem rådgivere, udførende og bygherre.",
              en: "We are based in Copenhagen, handling building engineering and coordination services for clients across the capital and throughout Zealand. We frequently work in North Zealand, where commercial developments, {link:renovering}, and specialized assignments require close coordination among designers, builders, and developers."
            },
            {
              type: "rich",
              da: "Når opgaven kræver det, kører vi ud i hele Danmark. Geografien ændrer ikke behovet for tydelige aftaler og en realistisk plan. Derfor tager vi altid udgangspunkt i projektets omfang, pladsens vilkår og vores aftalte rolle, før vi sammensætter forløbet og udarbejder et tilbud. Se også vores arbejde med {link:byggeprojekt}er og {link:projekter}.",
              en: "When the scope warrants it, we mobilize across all of Denmark. Geography never alters the necessity for transparent agreements and realistic schedules. We consistently tailor our framework to the project dimensions, site conditions, and agreed mandate before presenting an estimate. See also our work with {link:byggeprojekt}s and past {link:projekter}."
            }
          ],
          badges: {
            da: [
              "København & Frederiksberg",
              "Nordsjælland",
              "Hele Sjælland",
              "Hele Danmark efter aftale",
              "Erhvervsbyggeri",
              "Renovering & Ombygning",
              "Specialopgaver & Montage",
              "Rådgiverkoordinering"
            ],
            en: [
              "Copenhagen & Frederiksberg",
              "North Zealand",
              "Greater Zealand",
              "Nationwide on request",
              "Commercial Projects",
              "Renovation & Conversion",
              "Custom Builds & Mounting",
              "Consultant Coordination"
            ]
          }
        },
        {
          id: "consultation-and-quote",
          type: "callout",
          title: {
            da: "Få jeres byggeprojekt afklaret fra begyndelsen",
            en: "Get your construction project clarified from the start"
          },
          paragraphs: [
            {
              da: "Har I et byggeprojekt, som skal afklares, planlægges eller samles under tydelig projektledelse, tager vi gerne en indledende dialog. Fortæl os om behov, tidsramme og de fag, der allerede er involveret.",
              en: "If you have a construction project that needs clarification, planning, or unified project management, we welcome an exploratory conversation. Tell us about your needs, timeline, and the trades already engaged."
            },
            {
              type: "rich",
              da: "{link:kontakt-os} for en uforpligtende projektgennemgang og et tilbud baseret på opgavens omfang.",
              en: "{link:kontakt-os} for a non-binding project walkthrough and a proposal tailored to your actual scope."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Planlægning og forundersøgelse af byggeprojektet",
        "Afklaring af formål, omfang, tidsramme og faglige krav",
        "Teknisk afklaring før tegninger og udførelse",
        "Roller, ansvar og klare beslutningsveje",
        "Projektstyring og koordinering på byggepladsen",
        "Løbende opfølgning på kvalitet, aftaler og tidsplan"
      ],
      en: [
        "Construction project planning and pre-feasibility review",
        "Clarification of purpose, scope, timeline, and trade criteria",
        "Technical clarification prior to drafting and execution",
        "Stakeholder roles, responsibilities, and decision channels",
        "On-site project management and trade coordination",
        "Continuous monitoring of craftsmanship, agreements, and schedule"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Idé & Myndighedskrav", en: "Concept & Regulatory Check" },
        text: {
          da: "Vi analyserer lokalplaner, bygningsreglement (BR18) og tekniske krav for at klarlægge projektets lovmæssige rammer.",
          en: "We review local zoning, building regulations, and technical demands to identify regulatory parameters."
        }
      },
      {
        step: "02",
        title: { da: "Tegninger & Projektering", en: "Drafting & Engineering" },
        text: {
          da: "Udarbejdelse af præcise målfaste tegninger, snit og tekniske detaljer tilpasset de udførende faggrupper.",
          en: "Developing precise dimensioned drawings, sections, and technical details tailored for active builders."
        }
      },
      {
        step: "03",
        title: { da: "Byggetilladelse & Godkendelse", en: "Permit Acquisition" },
        text: {
          da: "Vi driver dialogen med kommunen og sikrer, at alle nødvendige tilladelser er på plads inden byggestart.",
          en: "We lead the dialogue with municipal authorities ensuring all requisite permits are secured before ground break."
        }
      },
      {
        step: "04",
        title: { da: "Udførelsesgrundlag & KS", en: "Execution Baseline & QA" },
        text: {
          da: "Færdigt udbudsmateriale og kontrolplaner overdrages til byggeledelsen, så udførelsen forløber gnidningsfrit.",
          en: "Final tender packages and inspection control plans are delivered to site leadership for seamless execution."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Fokus på bygbarhed", en: "Constructability Focus" },
        text: { da: "Vores praktiske baggrund sikrer løsninger, der reelt kan bygges uden unødige tillægsregninger.", en: "Our practical trade background ensures solutions can realistically be built without surprise variation orders." }
      },
      {
        title: { da: "Hurtig myndighedsbehandling", en: "Smooth Permitting" },
        text: { da: "Grundigt forberedte ansøgninger mindsker sagsbehandlingstiden hos de kommunale byggesagsafdelinger.", en: "Thoroughly prepared applications shorten approval cycles with municipal building departments." }
      },
      {
        title: { da: "Klar ansvarsfordeling", en: "Clear Accountability" },
        text: { da: "Præcise snitflader mellem faggrupper forhindrer misforståelser og forsinkelser.", en: "Explicit demarcation between trades eliminates misunderstandings and work stoppages." }
      }
    ],
    idealFor: {
      da: "Projektering er henvendt til bygherrer og virksomheder, der skal i gang med ombygning, nybyggeri eller tilbygning og har brug for et solidt teknisk grundlag.",
      en: "Engineering services cater to developers and property owners preparing for conversions, extensions, or new builds requiring robust technical groundwork."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-byggeri-under-udfoerelse-koebenhavn.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-arkitekttegnet-villa-traefacade.jpg",
    faq: [
      {
        q: {
          da: "Hvad indebærer projektering af byggeri helt konkret?",
          en: "What does building engineering and planning entail in practice?"
        },
        a: {
          da: "Projektering er den faglige proces, hvor bygherres ønsker og idéer omsættes til bygbare tegninger, tekniske beskrivelser og statiske beregninger. Vi kortlægger bygningens konstruktioner, installationer, materialevalg og myndighedskrav i detaljerede projektmapper. Projekteringen danner det præcise grundlag for både myndighedsgodkendelser, økonomistyring og det praktiske udførende håndværk. En grundig projektering forhindrer dyre omprojekteringer og forsinkelser, når håndværkerne går i gang på byggepladsen.",
          en: "Building engineering is the technical process where client aspirations and architectural concepts are translated into buildable drawings, technical specifications, and structural calculations. We map out the building's load-bearing assemblies, technical MEP installations, materials, and statutory requirements in comprehensive project dossiers. This engineering documentation forms the exact foundation for building permits, cost control, and physical trade execution. Rigorous initial engineering prevents costly site adjustments and unforeseen delays once physical construction begins."
        }
      },
      {
        q: {
          da: "Hjælper CPH Collective med byggetilladelse og myndighedsdialog?",
          en: "Does CPH Collective assist with building permits and authority liaison?"
        },
        a: {
          da: "Ja, vi håndterer hele ansøgningsprocessen hos den relevante kommune, herunder indsendelse af situationsplaner, facadetegninger og brandteknisk dokumentation. Vi følger sagen tæt og besvarer eventuelle tillægsspørgsmål fra kommunens sagsbehandlere for at sikre en hurtig sagsbehandling. Vores indgående kendskab til Bygningsreglementet (BR18) sikrer, at projektet lever op til alle gældende krav fra første færd.",
          en: "Yes, we oversee the entire permit application with municipal authorities, including submitting site plans, facade elevations, and fire safety documentation. We monitor the case closely and promptly address supplementary queries from municipal building officers to ensure swift approval. Our thorough command of the Danish Building Regulations (BR18) ensures your scheme satisfies all legal mandates from the outset."
        }
      },
      {
        q: {
          da: "Hvorfor er det en fordel, at projekteringen udføres af en entreprenørvirksomhed?",
          en: "Why is it beneficial for engineering to be handled by a builder-contractor?"
        },
        a: {
          da: "Traditionelle rådgivere designer ofte projekter uden tæt kontakt til den praktiske byggeplads, hvilket kan skabe uhensigtsmæssige og fordyrende løsninger. Hos CPH Collective tænker vi bygbarhed og håndværksmæssig realisme ind fra de første skitser. Vi kender materialernes reelle leveringstider, montagekrav og grænseflader mellem de enkelte faggrupper. Det resulterer i gennemtænkte projekter, der kan opføres mere rationelt, til tiden og inden for det aftalte budget.",
          en: "Traditional engineering consultants frequently draft blueprints without day-to-day exposure to physical jobsite conditions, which can lead to inefficient and costly detailing. At CPH Collective, we integrate constructability and practical trade knowledge into the earliest sketches. We understand actual material lead times, assembly constraints, and inter-trade sequencing on the floor. This results in well-engineered schemes that can be built more rationally, on schedule, and within the agreed budget."
        }
      },
      {
        q: {
          da: "Hvilket tegningsmateriale og hvilken dokumentation udarbejder I?",
          en: "What drawing documentation and technical material do you produce?"
        },
        a: {
          da: "Afhængigt af projektets omfang udarbejder vi dispositionsforslag, myndighedsprojekt og detaljerede hovedprojekter til udførelse. Dette inkluderer plan-, snit- og facadetegninger samt detaljetegninger af kritiske samlinger og konstruktioner. Vi leverer også arbejdsbeskrivelser, tidsplaner og nødvendige statiske eller energimæssige redegørelser. Alt materiale udleveres digitalt i overskuelige formater, som er lige til at bygge efter.",
          en: "Depending on project scale, we produce schematic designs, authority approval packages, and detailed construction documentation for execution. This encompasses floor plans, structural sections, elevations, and detail drawings of critical joints and junctions. We also deliver work specifications, progress milestones, and required structural or energy-efficiency calculations. All documentation is supplied digitally in standardized formats ready for immediate procurement and construction."
        }
      },
      {
        q: {
          da: "Kan I projektere et projekt, hvor andre håndværkere står for udførelsen?",
          en: "Can you provide engineering for projects built by other contractors?"
        },
        a: {
          da: "Ja, vi påtager os gerne den rene projekteringsopgave for bygherrer, der allerede har egne håndværkere eller udbyder opgaven i fagentrepriser. Vores detaljerede projektmateriale sikrer, at I modtager sammenlignelige og retvisende tilbud fra de bydende entreprenører. Skulle I efterfølgende ønske, at vi også udfører byggeriet eller fører fagtilsyn, kan vi ubesværet koble vores udførende afdeling på.",
          en: "Yes, we gladly execute standalone engineering for clients who already have preferred builders or intend to tender trade packages separately. Our comprehensive engineering dossiers ensure that you receive competitive, uniform, and dependable bids from tendering contractors. Should you subsequently wish for us to construct the scheme or conduct trade supervision, we can seamlessly connect our executing team."
        }
      }
    ]
  },
  {
    id: "carpentry",
    slug: {
      da: "toemrerarbejde",
      en: "carpentry"
    },
    title: {
      da: "Tømrerarbejde",
      en: "Carpentry Services"
    },
    heroHeading: {
      da: "Professionelt tømrerarbejde i København til krævende byggeprojekter",
      en: "Professional Carpentry in Copenhagen for Demanding Building Projects"
    },
    subtitle: {
      da: "Har I brug for en tømrer, der kan indgå smidigt og effektivt i et større byggeprojekt, hjælper CPH Collective med både planlægning og udførelse. Vi leverer professionelt tømrerarbejde i København og på Sjælland med fokus på præcision, koordinering og en løsning, der passer til projektets rammer.",
      en: "If you need a carpenter who can integrate smoothly and effectively into a larger construction project, CPH Collective assists with both planning and execution. We deliver professional carpentry in Copenhagen and Zealand focused on precision, coordination, and project-specific parameters."
    },
    seoTitle: {
      da: "Tømrerarbejde i København | CPH Collective",
      en: "Carpentry Services Copenhagen | CPH Collective"
    },
    seoDescription: {
      da: "Professionelt tømrerarbejde i København og på Sjælland. CPH Collective leverer konstruktioner, specialtømrerarbejde og montage.",
      en: "Professional carpentry in Copenhagen and Zealand. CPH Collective provides timber structures, custom joinery, and expert installation."
    },
    seoKeywords: {
      da: [
        "tømrerarbejde",
        "tømrer København",
        "professionelt tømrerarbejde",
        "tømrervirksomhed",
        "specialtømrerarbejde",
        "snedkerarbejde",
        "montagearbejde",
        "akustikpaneler montage",
        "specialinventar",
        "tagkonstruktioner",
        "tømrermester Sjælland",
        "tilbud på tømrerarbejde"
      ],
      en: [
        "carpentry services",
        "carpenter Copenhagen",
        "commercial carpentry",
        "carpentry company",
        "bespoke carpentry",
        "custom joinery",
        "installation services",
        "acoustic panel installation",
        "custom cabinetry",
        "roof construction",
        "carpenter Zealand",
        "carpentry quote"
      ]
    },
    description: {
      da: "Har I brug for en tømrer, der kan indgå smidigt og effektivt i et større byggeprojekt, hjælper CPH Collective med både planlægning og udførelse. Vi leverer professionelt tømrerarbejde i København og på Sjælland med fokus på præcision, koordinering og en løsning, der passer til projektets rammer.",
      en: "If you need a carpenter capable of integrating seamlessly into major building projects, CPH Collective provides planning and execution across Copenhagen and Zealand with emphasis on precision, coordination, and project-specific solutions."
    },
    longDescription: {
      da: [
        "Har I brug for en tømrer, der kan indgå smidigt og effektivt i et større byggeprojekt, hjælper CPH Collective med både planlægning og udførelse. Vi leverer professionelt tømrerarbejde i København og på Sjælland med fokus på præcision, koordinering og en løsning, der passer til projektets rammer.",
        "Som tømrervirksomhed ser vi ikke kun på den enkelte konstruktion. Vi forholder os også til tegninger, rækkefølge, adgangsforhold, andre fag og den fælles tidsplan. Det giver et mere sammenhængende forløb, uanset om vi har ansvar for en afgrænset leverance eller indgår i en totalentreprise."
      ],
      en: [
        "If you need a carpenter who can integrate smoothly and effectively into a larger construction project, CPH Collective assists with both planning and execution. We deliver professional carpentry in Copenhagen and Zealand with focus on precision, coordination, and solutions tailored to project boundaries.",
        "As a carpentry firm, we don't just look at isolated structures. We evaluate technical drawings, sequencing, site access, other trades, and the shared milestone schedule. This yields a cohesive process, whether delivering a targeted trade package or participating in a turnkey contract."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "holistic-understanding",
          type: "intro-callout",
          tag: {
            da: "01 / Helhed & Forståelse",
            en: "01 / Whole-Project Mindset"
          },
          title: {
            da: "Hvad får I med en tømrervirksomhed, der forstår hele projektet",
            en: "What you gain from a carpentry contractor who understands the whole project"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Som tømrervirksomhed ser vi ikke kun på den enkelte konstruktion. Vi forholder os også til tegninger, rækkefølge, adgangsforhold, andre fag og den fælles tidsplan. Det giver et mere sammenhængende forløb, uanset om vi har ansvar for en afgrænset leverance eller indgår i en {link:totalentreprise}.",
              en: "As a carpentry firm, we do not simply look at isolated timber structures. We evaluate technical drawings, sequencing, site access, adjacent trades, and the shared project schedule. This yields a cohesive workflow, whether we manage a specific trade package or participate in a {link:totalentreprise}."
            },
            {
              type: "rich",
              da: "Vi arbejder især for bygherrer, erhvervskunder, byggeledere og professionelle samarbejdspartnere. Her er tydelige aftaler og løbende dialog afgørende. I får en udførende partner, som forstår pladsens organisation og tager ansvar for, at eget arbejde spiller sammen med resten af byggeriet.",
              en: "We primarily partner with property owners, commercial clients, construction managers, and professional collaborators. Here, clear agreements and ongoing dialogue are paramount. You gain an executing partner who understands site logistics and ensures our trade work seamlessly complements the overall build."
            }
          ],
          calloutBox: {
            tag: { da: "Ansvar På Byggepladsen", en: "Site Accountability" },
            text: {
              da: "I får en udførende partner, som forstår pladsens organisation og tager ansvar for, at eget arbejde spiller sammen med resten af byggeriet.",
              en: "You get an executing partner who understands jobsite organization and takes accountability for harmonizing our trade deliverables with the entire build."
            }
          },
          stats: [
            {
              label: { da: "Bygherre & Partnere", en: "Clients & Partners" },
              value: { da: "Klar dialog & faste rammer", en: "Direct dialogue & set scope" }
            },
            {
              label: { da: "Pladsens Organisation", en: "Site Organization" },
              value: { da: "Samspil med andre fag", en: "Coordination with all trades" }
            },
            {
              label: { da: "Fremdrift & Tidsplan", en: "Progress & Timing" },
              value: { da: "Sammenhængende forløb", en: "Cohesive project flow" }
            }
          ]
        },
        {
          id: "carpentry-scope",
          type: "feature-grid",
          tag: {
            da: "02 / Fagområder & Leverancer",
            en: "02 / Trade Scope & Deliverables"
          },
          title: {
            da: "Hvilket tømrerarbejde kan vi tage ansvar for",
            en: "Carpentry services we take responsibility for"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Vores tømrerarbejde spænder fra bærende og indvendige konstruktioner til præcise detaljer og færdige overflader. Opgaven kan være en del af nybyggeri, {link:renovering} eller erhvervsindretning og bliver afklaret ud fra materialer, tekniske krav og den ønskede finish.",
              en: "Our carpentry spans load-bearing and interior framing to precise detailing and immaculate finishes. Deliverables fit new builds, {link:renovering}, or commercial fit-outs, scoped around materials, engineering specifications, and desired aesthetic quality."
            }
          ],
          cards: [
            {
              number: "01",
              title: {
                da: "Konstruktivt tømrerarbejde",
                en: "Structural Framing"
              },
              description: {
                da: "Bærende konstruktioner udført med rammer, spær, etageadskillelser og tagkonstruktioner til nybyggeri og ombygning.",
                en: "Structural timber framing, trusses, floor joists, and complete roof assemblies for new builds and extensions."
              },
              bullet: {
                da: "Rammer, spær, etageadskillelser og tagkonstruktioner",
                en: "Frames, trusses, floor separations, and roof structures"
              }
            },
            {
              number: "02",
              title: {
                da: "Indvendigt tømrerarbejde",
                en: "Interior Carpentry"
              },
              description: {
                da: "Præcist indvendigt arbejde med lofter, vægopbygning, træbeklædning og integrerede arkitektoniske elementer.",
                en: "Precise interior fit-out including ceilings, partitions, timber cladding, and integrated architectural woodwork."
              },
              bullet: {
                da: "Lofter, træbeklædning og arkitektoniske elementer",
                en: "Ceilings, wood cladding, and architectural features"
              }
            },
            {
              number: "03",
              title: {
                da: "Specialtømrerarbejde",
                en: "Custom Carpentry"
              },
              description: {
                da: "Skræddersyede løsninger med særlige krav til mål, design og montering ud fra komplekse arkitekttegninger.",
                en: "Bespoke woodwork demanding strict tolerances, intricate design criteria, and specialized mounting."
              },
              bullet: {
                da: "Løsninger med særlige krav til mål, design og montering",
                en: "Solutions with unique dimensional, design, and mounting demands"
              }
            },
            {
              number: "04",
              title: {
                da: "Facadearbejde & Montage",
                en: "Facade Cladding & Mounting"
              },
              description: {
                da: "Facadearbejde og præcis montering af træbaserede bygningsdele, der sikrer holdbarhed og et flot visuelt udtryk.",
                en: "Exterior timber facades and precision installation of wood-based building components with lasting durability."
              },
              bullet: {
                da: "Facadearbejde og præcis montering af træbaserede bygningsdele",
                en: "Facade carpentry and precise mounting of exterior timber assemblies"
              }
            }
          ]
        },
        {
          id: "special-carpentry",
          type: "architectural-box",
          tag: {
            da: "03 / Arkitektur & Skræddersyede Løsninger",
            en: "03 / Custom Architectural Solutions"
          },
          title: {
            da: "Når specialtømrerarbejde kræver mere end en standardløsning",
            en: "When bespoke carpentry demands more than off-the-shelf solutions"
          },
          paragraphs: [
            {
              da: "Specialtømrerarbejde er relevant, når en standardløsning ikke matcher arkitekturen, funktionen eller montageforholdene. Vi omsætter projektmateriale og særlige ønsker til en løsning, der kan produceres, tilpasses og monteres med blik for både konstruktionen og det færdige udtryk.",
              en: "Custom carpentry becomes essential when catalog products cannot meet the architecture, functional demands, or mounting constraints. We translate architectural drawings and unique design aspirations into solutions engineered, crafted, and mounted with precision."
            },
            {
              da: "Det kan være arkitektoniske elementer, akustiske træpaneler, historiske trækonstruktioner, facadebeklædning eller specialdesignede installationer. I sådanne opgaver samarbejder vi tæt med arkitekter, designere og projektledere, så detaljerne fungerer i praksis og på den konkrete byggeplads.",
              en: "Examples include architectural features, acoustic timber panels, heritage timber restoration, facade cladding, or bespoke artistic installations. In these commissions, we collaborate closely with architects, interior designers, and project managers so details excel both in concept and on the live jobsite."
            }
          ],
          badges: {
            da: [
              "Arkitektoniske elementer",
              "Akustiske træpaneler",
              "Historiske trækonstruktioner",
              "Facadebeklædning",
              "Specialdesignede installationer"
            ],
            en: [
              "Architectural features",
              "Acoustic timber panels",
              "Heritage timber structures",
              "Exterior facade cladding",
              "Custom-designed installations"
            ]
          }
        },
        {
          id: "joinery-and-millwork",
          type: "materials-box",
          tag: {
            da: "04 / Snedkeri & Specialinventar",
            en: "04 / Custom Millwork & Joinery"
          },
          title: {
            da: "Snedkerarbejde og specialinventar med blik for helheden",
            en: "Cabinetmaking and custom millwork with an eye for the whole"
          },
          paragraphs: [
            {
              da: "Med snedkerarbejde og specialinventar skaber vi løsninger til rum, hvor mål, materialer og samlinger har stor betydning. Det gælder blandt andet køkkener, receptionsskranker, skuffer, lofter og andet fast inventar, som skal passe præcist til bygningen og den daglige anvendelse.",
              en: "Through bespoke joinery and custom cabinetry, we build spatial solutions where dimensions, material choice, and joinery details matter. This includes bespoke kitchens, reception desks, built-in storage, feature ceilings, and fixed millwork engineered to integrate flawlessly into the architecture and daily workflows."
            },
            {
              da: "Vi har blandt andet arbejdet med massiv røget eg, håndlavede samlinger, egetræslameller og lydabsorberende filt. Materialet vælges til det enkelte projekt. Vores rolle er at sikre en omhyggelig bearbejdning og montage, så inventar og bygningsdele opleves som en naturlig del af helheden.",
              en: "Our craft portfolio includes solid smoked oak, handcrafted joints, architectural oak slats, and acoustic sound-absorbing felt. Materials are curated per project. Our mission is delivering rigorous fabrication and installation, making built fixtures and structural elements feel like an organic extension of the environment."
            }
          ],
          materials: {
            label: {
              da: "Fremhævede materialer & finish",
              en: "Featured Materials & Craftsmanship"
            },
            items: {
              da: [
                "Massiv røget eg",
                "Håndlavede samlinger",
                "Egetræslameller",
                "Lydabsorberende filt"
              ],
              en: [
                "Solid smoked oak",
                "Handmade joinery joints",
                "Architectural oak slats",
                "Sound-absorbing acoustic felt"
              ]
            }
          }
        },
        {
          id: "mounting-and-logistics",
          type: "checklist",
          tag: {
            da: "05 / Montage & Pladskoordinering",
            en: "05 / Installation & Coordination"
          },
          title: {
            da: "Montagearbejde med styr på pladsen og de øvrige fag",
            en: "Installation work with site control and trade coordination"
          },
          paragraphs: [
            {
              da: "Montagearbejde kræver mere end at få et element på plads. Mål, tolerancer, adgang, rækkefølge og samspillet med andre fag skal være afklaret. Vi udfører montage af specialdesignede elementer, akustikløsninger og bygningsdele, hvor præcision og ro i udførelsen er afgørende.",
              en: "Mounting and installation require far more than merely positioning a component. Dimensional tolerances, accessibility, phasing, and interaction with adjacent contractors must be fully scoped. We execute installations of custom elements, acoustic systems, and building assemblies where precision and calm execution are paramount."
            },
            {
              type: "rich",
              da: "På aktive byggepladser koordinerer vi vores indsats med byggeledelsen og de øvrige fag. Har projektet brug for mere praktisk understøttelse, kan tømrerleverancen kombineres med vores {link:byggepladsservice}. Det gør grænsefladerne tydeligere og hjælper arbejdet videre efter den aftalte plan.",
              en: "On active jobsites, we coordinate our crews alongside construction management and other contractors. Should the project require supplementary site support, carpentry packages can be coupled with our {link:byggepladsservice}, establishing crystal-clear work boundaries and driving project momentum."
            }
          ],
          bullets: {
            da: [
              "Afklaring af tegninger, mål, materialer og grænseflader",
              "Planlægning af adgang, rækkefølge og koordinering med andre fag",
              "Udførelse, løbende kvalitetssikring og aflevering af den aftalte leverance"
            ],
            en: [
              "Clarification of drawings, measurements, materials, and trade interfaces",
              "Logistical planning of access, task sequencing, and trade coordination",
              "Flawless execution, ongoing QA verification, and clean handover"
            ]
          }
        },
        {
          id: "nationwide-coverage",
          type: "coverage",
          tag: {
            da: "06 / Geografi & Kapacitet",
            en: "06 / Geographical Reach & Scope"
          },
          title: {
            da: "Tømrer i København, på Sjælland og i hele Danmark",
            en: "Carpentry in Copenhagen, Zealand, and across Denmark"
          },
          paragraphs: [
            {
              da: "CPH Collective har base i København og udfører tømrerarbejde i hovedstaden, på Sjælland og i Nordsjælland. Søger I en tømrer i København eller en fast udførende partner på tværs af flere lokationer, tager vi gerne dialogen om den konkrete opgave.",
              en: "CPH Collective is based in Copenhagen, conducting high-caliber carpentry in the capital, Zealand, and North Zealand. If you seek a carpentry contractor in Copenhagen or an enduring construction partner across multiple locations, we welcome an initial consultation."
            },
            {
              type: "rich",
              da: "Vi kører også ud til opgaver i hele Danmark, når projektets omfang kalder på vores kompetencer. Det er især relevant for professionelle kunder med flere lokationer eller et projektteam, som ønsker samme samarbejdspartner gennem flere faser og på tværs af byggepladser. Se eksempler på vores arbejde under {link:projekter}.",
              en: "We also mobilize nationwide across Denmark whenever a project's scale and standard require our specific competencies. This is particularly advantageous for commercial clients with multi-site rollouts or developers seeking consistent craftsmanship across project phases. View past project deliveries under {link:projekter}."
            }
          ],
          badges: {
            da: [
              "København & Hovedstaden",
              "Sjælland & Nordsjælland",
              "Hele Danmark",
              "Nybyggeri & Erhverv",
              "Specialtømrer & Snedkeri",
              "Akustik & Montage"
            ],
            en: [
              "Copenhagen & Capital Region",
              "Zealand & North Zealand",
              "Nationwide Denmark",
              "New Builds & Commercial",
              "Custom Carpentry & Joinery",
              "Acoustics & Precision Mounting"
            ]
          }
        },
        {
          id: "quote-and-dialogue",
          type: "callout",
          tag: {
            da: "07 / Tilbud & Dialog",
            en: "07 / Quote & Consultation"
          },
          title: {
            da: "Hvordan får I et tilbud på tømrerarbejde",
            en: "How to obtain a carpentry quote"
          },
          paragraphs: [
            {
              da: "Prisen på tømrerarbejde afhænger af opgavens omfang, materialer, adgangsforhold, tidsplan og krav til finish. Derfor gennemgår vi gerne projektmaterialet med jer, før vi afgrænser leverancen og udarbejder et tilbud.",
              en: "Pricing for carpentry work depends on project scope, materials, site access, scheduling, and finish standards. We are eager to review your drawings and specifications before defining deliverables and submitting a detailed proposal."
            },
            {
              type: "rich",
              da: "{link:kontakt} for en uforpligtende dialog om tømrerarbejde, specialinventar eller montagearbejde.",
              en: "{link:kontakt} for a non-binding dialogue regarding carpentry, bespoke joinery, or mounting services."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Bærende trækonstruktioner, spær og facadeopbygning",
        "Indvendige vægge, gipsarbejde og akustiklofter",
        "Montage af døre, vinduer og glaspartier",
        "Akustikpaneler og specialdesignede trælameller",
        "Specialinventar og fast inventarmontage",
        "Kvalitetssikret efterbehandling og finish"
      ],
      en: [
        "Load-bearing timber framing, trusses, and exterior facade builds",
        "Interior stud walls, drywall systems, and acoustic ceiling panels",
        "Installation of high-performance doors, windows, and glass screens",
        "Acoustic paneling and architectural slat timber cladding",
        "Bespoke cabinetry and built-in fixtures",
        "Quality-assured detailing and final touch-ups"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Gennemgang & Opmåling", en: "Review & Precision Survey" },
        text: {
          da: "Nøjagtig opmåling på byggepladsen og faglig vurdering af konstruktive forudsætninger.",
          en: "Laser-accurate site measurement and trade assessment of existing structural conditions."
        }
      },
      {
        step: "02",
        title: { da: "Materialevalg & Klargøring", en: "Material Selection & Prep" },
        text: {
          da: "Udvælgelse af bæredygtige, certificerede træsorter og forberedelse af præfabrikerede elementer.",
          en: "Selection of sustainable, certified timber and prefabrication of customized components."
        }
      },
      {
        step: "03",
        title: { da: "Faglig Montering", en: "Skilled Installation" },
        text: {
          da: "Udførelse udført af uddannede tømrere med fokus på millimeterpræcision og rene samlinger.",
          en: "Execution conducted by seasoned carpenters with millimeter precision and clean joins."
        }
      },
      {
        step: "04",
        title: { da: "Kvalitetskontrol & Aflevering", en: "Quality Audit & Handover" },
        text: {
          da: "Systematisk kontrol af finish, fuger, funktioner og aflevering uden udeståender.",
          en: "Systematic review of finishes, seals, operations, and zero-defect handover."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Førsteklasses finish", en: "Exquisite Finish" },
        text: { da: "Detaljerne gør forskellen. Vi leverer et ensartet og eksklusivt resultat hver gang.", en: "Details define the outcome. We deliver flawless, consistent results on every build." }
      },
      {
        title: { da: "Respekt for tidsplaner", en: "Schedule Discipline" },
        text: { da: "Vores tømrerhold arbejder disciplineret og sikrer, at efterfølgende fag kan rykke på plads til tiden.", en: "Our carpentry teams operate with discipline, keeping subsequent trades on schedule." }
      },
      {
        title: { da: "Akustisk ekspertise", en: "Acoustic Expertise" },
        text: { da: "Specialiseret viden om montering af moderne træakustikpaneler i erhvervslokaler.", en: "Specialized knowledge in installing modern acoustic wood systems in commercial spaces." }
      }
    ],
    idealFor: {
      da: "Vores tømrerarbejde henvender sig til hovedentreprenører, arkitekter, bygherrer og virksomheder, der forventer kompromisløs kvalitet.",
      en: "Our carpentry services are tailored for general contractors, architects, developers, and enterprises expecting uncompromising build standards."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-snedkerarbejde-specialdesignet-traeinterioer.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-kontordomicil-lobby-traebeklaedning.jpg",
    faq: [
      {
        q: {
          da: "Hvilke typer tømrer- og snedkeropgaver udfører I i København og på Sjælland?",
          en: "What types of carpentry and joinery projects do you undertake?"
        },
        a: {
          da: "Vi udfører en bred vifte af opgaver, der spænder fra bærende trækonstruktioner, tagudskiftninger og facader til indvendig aptering og specialsnedkeri. Vores tømrere har stor erfaring med både fredede københavnske ejendomme, moderne erhvervsbyggeri og arkitekttegnede privatboliger. Vi leverer skræddersyede løsninger, såsom specialbyggede akustikpaneler, trapper, skabsarrangementer og receptionsskranker. Uanset opgavens karakter vægter vi finish, præcision og holdbare materialevalg højt.",
          en: "We execute an extensive range of scopes, spanning structural timber framing, roof replacements, and exterior facades to bespoke interior fit-outs and architectural joinery. Our carpenters possess seasoned experience in listed Copenhagen heritage properties, contemporary commercial builds, and architect-designed private residences. We deliver tailored solutions such as custom acoustic paneling, staircases, integrated cabinetry, and sculptural reception counters. Regardless of scope, we place paramount value on meticulous finish, precision tolerances, and durable materials."
        }
      },
      {
        q: {
          da: "Arbejder I ud fra eksisterende arkitekttegninger, eller kan I rådgive om løsningen?",
          en: "Do you build from existing blueprints, or do you provide technical advice?"
        },
        a: {
          da: "Vi arbejder rutineret ud fra færdige arkitekttegninger og rådgiver gerne om optimering af detaljer, materialer og montagemetoder. Har I ikke tegninger i forvejen, kan vi bistå med faglig sparring, opmåling og forslag til konstruktive løsninger. Vores mål er altid at forene arkitektonisk æstetik med solid bygbarhed og lang levetid.",
          en: "We routinely execute directly from architect-approved drawings while providing constructive advice on detailing, material choices, and assembly techniques. If drawings have not yet been produced, we can assist with technical sparring, site measurements, and structural recommendations. Our objective is always to synthesize architectural aesthetics with dependable constructability and lasting durability."
        }
      },
      {
        q: {
          da: "Hvilke materialer og træsorter anvender I fortrinsvis?",
          en: "Which materials and timber species do you predominantly utilize?"
        },
        a: {
          da: "Vi lægger stor vægt på bæredygtighed og anvender primært certificeret FSC- eller PEFC-mærket træ af højeste kvalitet. Til udendørs konstruktioner vælger vi bestandige og velegnede træsorter, der tåler det omskiftelige nordiske klima med minimal vedligeholdelse. Til indendørs snedkerarbejde rådgiver vi om hårdttræ, finerede elementer eller specialbehandlinger, der harmonerer med rummets øvrige arkitektur. Vi vejleder altid grundigt om materialernes egenskaber, slidstyrke og fremtidige pleje.",
          en: "We place strong emphasis on sustainability and primarily utilize premium certified timber bearing FSC or PEFC chain-of-custody labels. For exterior assemblies, we select naturally resilient timber species engineered to withstand harsh Nordic weather with minimal maintenance. For interior joinery, we guide clients through hardwoods, tailored veneers, or specialized surface finishes that complement the ambient architecture. We always advise in detail on mechanical characteristics, abrasion resistance, and maintenance protocols."
        }
      },
      {
        q: {
          da: "Hvor hurtigt kan I rykke ud til et tømrerprojekt?",
          en: "How quickly can you mobilize for a carpentry project?"
        },
        a: {
          da: "Vores opstartstid afhænger af projektets karakter og det aktuelle ordrebogsniveau, men vi tilstræber altid en hurtig og fleksibel proces. Mindre tømreropgaver og hastesager kan vi ofte påbegynde inden for få uger efter besigtigelse og aftaleindgåelse. Ved større projekter planlægger vi opstarten i god tid, så materialer og mandskab er fuldstændigt afstemt med byggeplanen.",
          en: "Mobilization timelines depend on the specific project scope and current workshop capacity, though we consistently prioritize flexibility. Smaller carpentry tasks and urgent requirements can often commence within a few weeks following site inspection and formal contract finalization. For larger commercial undertakings, we plan milestones well in advance to align material procurement and specialist crews with master schedules."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I, at det udførte tømrerarbejde holder i mange år?",
          en: "How do you ensure that carpentry installations endure for decades?"
        },
        a: {
          da: "Vores tømrersvende er alle faguddannede med dyb respekt for traditionelle håndværksdyder kombineret med moderne montageteknikker. Vi overholder gældende branchestandarder, fugtkrav og tolerancespecifikationer for alt udført tømrerarbejde. Undervejs udfører vi systematisk egenkontrol og fotodokumentation af skjulte konstruktioner før lukning. Dette sikrer sunde, velisolerede og formstabile konstruktioner, som I kan have glæde af i årtier.",
          en: "Our carpenters are fully certified journeymen with deep respect for traditional craft traditions combined with modern structural assembly methods. We adhere strictly to prevailing Danish trade codes, moisture barriers, and dimensional tolerances across all carpentry works. Throughout each phase, we perform systematic quality checks and photographic documentation of concealed framing prior to enclosing walls. This guarantees sound, well-insulated, and structurally stable timber solutions that remain flawless for decades."
        }
      }
    ]
  },
  {
    id: "renovation",
    slug: {
      da: "renovering",
      en: "renovation"
    },
    title: {
      da: "Renovering & Ombygning",
      en: "Renovation & Remodeling"
    },
    heroHeading: {
      da: "Renovering i København med styr på hele forløbet",
      en: "Renovation in Copenhagen with Complete Project Control"
    },
    subtitle: {
      da: "Skal eksisterende lokaler eller en ejendom have nyt liv, hjælper CPH Collective med renovering fra den første afklaring til den færdige udførelse. Vi kombinerer projektledelse med praktisk håndværk, så ansvar, rækkefølge og kvalitet hænger sammen gennem hele opgaven.",
      en: "Should existing premises or a building need new life, CPH Collective manages renovation from initial scoping to finished delivery. We unite project management with hands-on craftsmanship so responsibility, sequence, and quality align throughout."
    },
    seoTitle: {
      da: "Professionel renovering i København | CPH Collective",
      en: "Renovation Copenhagen | CPH Collective"
    },
    seoDescription: {
      da: "CPH Collective leverer totalentreprise, projektering og tømrerarbejde i København og på Sjælland. Få et uforpligtende tilbud.",
      en: "Renovation and remodeling in Copenhagen from scoping to handover. CPH Collective combines disciplined management with craftsmanship."
    },
    seoKeywords: {
      da: [
        "renovering",
        "ombygning",
        "renovering København",
        "renovering i København",
        "totalrenovering København",
        "erhvervsrenovering",
        "erhvervsindretning",
        "restaurering",
        "tømrerarbejde renovering",
        "totalentreprise renovering",
        "renovering af hus",
        "tilbud renovering"
      ],
      en: [
        "renovation",
        "remodeling",
        "renovation Copenhagen",
        "commercial renovation",
        "historic restoration",
        "turnkey renovation",
        "office fit-out",
        "residential remodeling",
        "renovation quote"
      ]
    },
    description: {
      da: "Skal eksisterende lokaler eller en ejendom have nyt liv, hjælper CPH Collective med renovering fra den første afklaring til den færdige udførelse. Vi kombinerer projektledelse med praktisk håndværk, så ansvar, rækkefølge og kvalitet hænger sammen gennem hele opgaven.",
      en: "CPH Collective manages renovations from initial scoping to final handover, uniting disciplined project management with practical craftsmanship."
    },
    longDescription: {
      da: [
        "Skal eksisterende lokaler eller en ejendom have nyt liv, hjælper CPH Collective med renovering fra den første afklaring til den færdige udførelse. Vi kombinerer projektledelse med praktisk håndværk, så ansvar, rækkefølge og kvalitet hænger sammen gennem hele opgaven.",
        "En vellykket renovering begynder med at forstå bygningen, brugernes behov og de forhold, der kan påvirke arbejdet. Vi gennemgår derfor omfang, adgang, materialer og afhængigheder, før udførelsen går i gang. Det giver et bedre grundlag for tidsplan, økonomi og koordinering."
      ],
      en: [
        "Should existing premises or a property need new life, CPH Collective manages renovation from initial scoping to finished delivery, uniting disciplined project management with high craftsmanship.",
        "A successful renovation begins with understanding the building, the occupants' requirements, and specific site factors. We review scope, accessibility, materials, and trade dependencies before execution begins."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "scope-and-clarification",
          type: "intro-callout",
          tag: {
            da: "01 / Forundersøgelse & Afklaring",
            en: "01 / Scoping & Assessment"
          },
          title: {
            da: "Når eksisterende rammer skal fungere bedre",
            en: "When Existing Spaces Need to Perform Better"
          },
          paragraphs: [
            {
              da: "En vellykket renovering begynder med at forstå bygningen, brugernes behov og de forhold, der kan påvirke arbejdet. Vi gennemgår derfor omfang, adgang, materialer og afhængigheder, før udførelsen går i gang. Det giver et bedre grundlag for tidsplan, økonomi og koordinering.",
              en: "A successful renovation begins with understanding the building, user needs, and the specific conditions that can influence the work. We therefore review scope, site access, materials, and dependencies before execution commences, providing a sound foundation for schedule, budget, and coordination."
            }
          ],
          calloutBox: {
            tag: {
              da: "Pris & Økonomisk Ramme",
              en: "Pricing & Cost Framework"
            },
            text: {
              da: "Prisen på en renovering afhænger af bygningens stand, opgavens omfang, materialevalg, adgangsforhold og antallet af fag. Derfor giver vi ikke en standardpris. Vi afklarer behovet og udarbejder et tilbud, der tager udgangspunkt i det konkrete projekt og den ønskede løsning.",
              en: "The cost of a renovation depends on the building's condition, scope of work, material selection, site access, and the number of active trades. We do not provide standard boilerplate prices; instead, we clarify requirements and prepare a custom proposal grounded in the specific project and desired solution."
            }
          },
          stats: [
            {
              label: { da: "Bygningens stand", en: "Structure Survey" },
              value: { da: "Grundig registrering", en: "Full inspection" }
            },
            {
              label: { da: "Pris & Tilbud", en: "Pricing Model" },
              value: { da: "Individuelt tilpasset", en: "Tailored quote" }
            },
            {
              label: { da: "Tidsplan & Fag", en: "Timeline & Trades" },
              value: { da: "Fastlagt rækkefølge", en: "Defined sequencing" }
            }
          ]
        },
        {
          id: "renovation-scopes",
          type: "checklist",
          tag: {
            da: "02 / Fagområder & Leverancer",
            en: "02 / Trade Packages & Delivery"
          },
          title: {
            da: "Hvilke dele af renoveringen kan vi løse",
            en: "Which Scopes of the Renovation Can We Deliver"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Som bygge og tømrervirksomhed kan vi både indgå som udførende partner og tage et bredere ansvar for koordineringen. Ved større forløb kan en {link:totalentreprise} samle planlægning og udførelse hos ét kontaktpunkt, mens afgrænsede opgaver kan løses som fagleverancer og montageopgaver.",
              en: "As a construction and carpentry company, we can serve as executing craftsmen or shoulder broader coordination duties. On larger projects, a {link:totalentreprise} unifies planning and execution under one point of contact, while defined packages can be delivered as trade contracts and installation."
            }
          ],
          bulletsLabel: {
            da: "Centrale fagleverancer og kompetencer",
            en: "Core trade deliverables and capabilities"
          },
          bullets: {
            da: [
              "Tømrerarbejde ved ombygning, lofter, beklædning og konstruktive ændringer",
              "Snedkerarbejde og specialinventar til boliger, kontorer, receptioner og fællesområder",
              "Erhvervsindretning med akustikpaneler, trælameller og specialtilpassede elementer",
              "Restaurering, facadearbejde og montage med respekt for bygningens udtryk"
            ],
            en: [
              "Carpentry for remodeling, ceilings, claddings, and structural modifications",
              "Joinery and custom cabinetry for residential homes, offices, receptions, and common areas",
              "Commercial fit-outs with acoustic paneling, wood slats, and bespoke architectural features",
              "Heritage restoration, facade refurbishment, and mounting respecting the building's aesthetic"
            ]
          }
        },
        {
          id: "project-on-track",
          type: "dual-cards",
          tag: {
            da: "03 / Planlægning, Snitflader & Styring",
            en: "03 / Planning, Interfaces & Project Control"
          },
          title: {
            da: "Sådan holder vi jeres byggeprojekt på rette spor",
            en: "How We Keep Your Construction Project on Track"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Et renoveringsprojekt rummer ofte flere fag, eksisterende konstruktioner og forhold, som først bliver synlige undervejs. Vi planlægger arbejdet i en logisk rækkefølge og koordinerer med bygherre, rådgivere og øvrige aktører. Det mindsker uklare grænseflader på den aktive byggeplads.",
              en: "A renovation project frequently involves multiple trades, existing load-bearing structures, and unforeseen factors revealed during the works. We plan activities in a logical sequence and coordinate closely with clients, consultants, and active contractors to eliminate ambiguous interfaces on site."
            }
          ],
          cards: [
            {
              level: "h3",
              tag: {
                da: "Fase 01 · Forberedelse",
                en: "Phase 01 · Scoping"
              },
              title: {
                da: "Før udførelsen skaber vi et klart grundlag",
                en: "Before execution, we establish a clear foundation"
              },
              paragraphs: [
                {
                  da: "Vi starter med at gennemgå projektmaterialet og afklare vores rolle, leverancer og snitflader. Ved behov bidrager vi til planlægning og praktisk projektafklaring. Målet er, at alle kender rækkefølgen, ansvaret og de krav, som udførelsen skal leve op til, før arbejdet sættes i gang.",
                  en: "We start by reviewing project documentation and defining our role, deliverables, and interface points. When needed, we contribute to planning and practical constructability reviews. The objective is ensuring all parties know the sequence, responsibilities, and standards before site work begins."
                }
              ],
              bullets: {
                da: [
                  "Gennemgang af projektmateriale og snitflader",
                  "Klarhed over rækkefølge, ansvar og specifikke krav",
                  "Bidrag til planlægning og praktisk projektafklaring"
                ],
                en: [
                  "Review of project specifications and trade interfaces",
                  "Clarity on sequencing, accountability, and technical demands",
                  "Active contribution to pre-construction planning and constructability"
                ]
              }
            },
            {
              level: "h3",
              tag: {
                da: "Fase 02 · Fremdrift",
                en: "Phase 02 · Momentum"
              },
              title: {
                da: "Under arbejdet samler vi trådene",
                en: "During work, we tie the threads together"
              },
              paragraphs: [
                {
                  type: "rich",
                  da: "Når udførelsen begynder, følger vi fremdriften og tilpasser koordineringen til forholdene på pladsen. Vi holder fokus på adgang, sikkerhed, materialeleverancer og samarbejdet mellem fagene. Samtidig sikrer løbende kommunikation, at ændringer bliver håndteret, før de påvirker resten af forløbet.",
                  en: "When execution begins, we monitor momentum and adapt coordination to real-world jobsite conditions. We maintain focus on site access, safety, material logistics, and trade synergy, while continuous communication ensures adjustments are resolved before impacting the overall build."
                }
              ],
              bullets: {
                da: [
                  "Fokus på adgangsforhold, sikkerhed og materialeleverancer",
                  "Tæt tværfagligt samarbejde og koordinering på pladsen",
                  "Løbende kommunikation så ændringer håndteres i tide"
                ],
                en: [
                  "Focus on access routes, site safety, and material deliveries",
                  "Tight interdisciplinary collaboration and jobsite coordination",
                  "Continuous dialogue resolving variations promptly"
                ]
              }
            }
          ]
        },
        {
          id: "restoration-and-fitout",
          type: "architectural-box",
          tag: {
            da: "04 / Håndværk, Detalje & Finish",
            en: "04 / Craftsmanship, Detailing & Finish"
          },
          title: {
            da: "Restaurering og erhvervsindretning kræver blik for detaljen",
            en: "Restoration and Commercial Fit-Out Demand an Eye for Detail"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Ved restaurering skal nye indgreb passe til bygningens konstruktion og oprindelige karakter. Vi kan arbejde med eksisterende trækonstruktioner, genskabe bygningsdele efter historisk materiale og udføre præcist {link:toemrerarbejde}, hvor både styrke, samlinger og det visuelle udtryk har betydning.",
              en: "In historic restoration, new interventions must respect the building's original anatomy and character. We preserve and reinforce timber frameworks, reconstruct architectural details from historical records, and deliver precise {link:toemrerarbejde} where joinery, structural strength, and aesthetics matter."
            },
            {
              type: "rich",
              da: "Erhvervsindretning handler både om funktion, akustik og en finish, der passer til virksomhedens rammer. Vi udfører blandt andet træbeklædning, akustiske lofter, receptionsløsninger og specialinventar. Arbejdet planlægges, så installationer, andre fag og den daglige brug af lokalerne tænkes ind.",
              en: "Commercial fit-outs balance function, acoustics, and an elevated finish that mirrors corporate identity. We fabricate and install timber wall claddings, acoustic ceilings, bespoke reception environments, and custom cabinetry—planned so building services, neighboring trades, and day-to-day office routines integrate seamlessly."
            }
          ],
          badgesLabel: {
            da: "Faglige løsninger og specialmontage",
            en: "Specialized solutions and craftsman mounting"
          },
          badges: {
            da: [
              "Historiske trækonstruktioner",
              "Genskabelse efter historisk materiale",
              "Træbeklædning & akustiske lofter",
              "Receptionsløsninger",
              "Specialinventar & finish",
              "Præcist tømrerarbejde"
            ],
            en: [
              "Heritage timber structures",
              "Replication of period details",
              "Timber claddings & acoustic ceilings",
              "Reception installations",
              "Custom joinery & bespoke fixtures",
              "Precision carpentry"
            ]
          }
        },
        {
          id: "nationwide-coverage",
          tag: {
            da: "05 / Geografi & Projekttyper",
            en: "05 / Geography & Coverage"
          },
          title: {
            da: "Renovering af hus fra København til resten af Danmark",
            en: "Renovating Properties from Copenhagen to Across Denmark"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Ved renovering af hus kan vi udføre udvalgte tømrer og snedkeropgaver eller indgå i et større samlet forløb. Hvis hele boligen skal ombygges, kan en {link:totalrenovering} give bedre sammenhæng mellem konstruktioner, materialer, indretning og de involverede fag.",
              en: "When renovating homes, we can deliver selected carpentry and joinery scopes or enter into a comprehensive full-house program. If an entire residence requires remodeling, a {link:totalrenovering} creates superior cohesion between structural framing, materials, interior detailing, and the involved trades."
            },
            {
              type: "rich",
              da: "Vi har base i København og løser renoveringsopgaver på hele Sjælland og i Nordsjælland. Ved relevante projekter kører vi også ud i resten af Danmark. Det gælder især erhvervsbyggeri, specialopgaver og {link:byggeprojekt}er, hvor vores kombination af koordinering og praktisk udførelse skaber værdi. Se eksempler på vores arbejde under {link:projekter}.",
              en: "Headquartered in Copenhagen, we execute renovation projects throughout Zealand and North Zealand. On fitting projects, we mobilize across Denmark—particularly for commercial builds, architectural specialty projects, and {link:byggeprojekt}s where our blend of coordination and craftsmanship delivers outstanding value. Explore past work in {link:projekter}."
            }
          ],
          badges: {
            da: [
              "København & Frederiksberg",
              "Nordsjælland & Kysten",
              "Roskilde & Midtsjælland",
              "Hele Sjælland",
              "Erhvervsbyggeri & Kontor",
              "Renovering af hus & villaer",
              "Hele Danmark efter aftale"
            ],
            en: [
              "Copenhagen & Frederiksberg",
              "North Zealand & Coast",
              "Roskilde & Central Zealand",
              "Zealand region",
              "Commercial offices & buildings",
              "House remodeling & private homes",
              "Nationwide by agreement"
            ]
          }
        },
        {
          id: "ready-for-clarification",
          level: "h2",
          type: "callout",
          title: {
            da: "Klar til at få renoveringen afklaret",
            en: "Ready to Clarify Your Renovation?"
          },
          paragraphs: [
            {
              da: "Har I planer om at ændre en ejendom, indrette erhvervslokaler eller restaurere eksisterende bygningsdele, tager vi gerne en indledende samtale om opgaven.",
              en: "If you have plans to transform a commercial property, fit out corporate offices, or restore historic architectural components, we welcome an initial consultation."
            },
            {
              type: "rich",
              da: "{link:kontakt-os} og fortæl om jeres behov, så vi kan vurdere omfang, rolle og næste skridt.",
              en: "{link:kontakt-os} and tell us about your project requirements so we can assess scope, our role, and the immediate next steps."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Tømrerarbejde ved ombygning, lofter, beklædning og konstruktive ændringer",
        "Snedkerarbejde og specialinventar til boliger, kontorer og receptioner",
        "Erhvervsindretning med akustikpaneler, trælameller og specialelementer",
        "Restaurering, facadearbejde og montage med respekt for udtryk",
        "Totalentreprise og samlet styring af alle fag ved større forløb",
        "Afklaring af omfang, adgangsforhold, materialer og tilbud"
      ],
      en: [
        "Carpentry for remodeling, ceilings, claddings, and structural modifications",
        "Joinery and bespoke fixtures for residences, offices, and receptions",
        "Commercial fit-out with acoustic panels, wood slats, and custom elements",
        "Heritage restoration, facade work, and installation respecting character",
        "Turnkey management and single-point coordination for major projects",
        "Clarification of scope, site access, materials, and comprehensive quoting"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Tilstandsvurdering", en: "Condition Survey" },
        text: {
          da: "Grundig registrering af bygningens eksisterende stand, bærende elementer og skjulte udfordringer.",
          en: "Rigorous assessment of existing structural health, load-bearing parts, and latent challenges."
        }
      },
      {
        step: "02",
        title: { da: "Renoveringsstrategi", en: "Renovation Strategy" },
        text: {
          da: "Vi fastlægger en detaljeret handlingsplan med respekt for bygningens arkitektur og budget.",
          en: "We map out an actionable step-by-step strategy respecting historic architecture and budget."
        }
      },
      {
        step: "03",
        title: { da: "Nænsom Udførelse", en: "Mindful Execution" },
        text: {
          da: "Vores håndværkere renoverer med stor præcision og beskytter bevaringsværdige bygningsdele.",
          en: "Our carpenters renovate with great dexterity, safeguarding preservation-worthy elements."
        }
      },
      {
        step: "04",
        title: { da: "Moderne Opgradering", en: "Modern Performance Delivery" },
        text: {
          da: "Bygningen afleveres med nutidig akustik, indeklima og æstetik klar til daglig drift.",
          en: "Building handed over with contemporary acoustic, thermal, and visual performance ready for operation."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Respekt for historien", en: "Heritage Preservation" },
        text: { da: "Vi forstår traditionelt håndværk og forener det smukt med moderne funktionalitet.", en: "We appreciate traditional techniques and marry them smoothly with modern technology." }
      },
      {
        title: { da: "Værdiforøgelse", en: "Value Enhancement" },
        text: { da: "En gennemtænkt renovering forbedrer ejendommens markedsværdi og brugsværdi mærkbart.", en: "Strategic refurbishment substantially elevates property valuation and usable utility." }
      },
      {
        title: { da: "Bæredygtig tilgang", en: "Sustainable Lifecycle" },
        text: { da: "Bevaring og opgradering af eksisterende bygninger sparer store mængder CO2 frem for nybyggeri.", en: "Upgrading existing structures preserves embodied carbon and minimizes environmental impact." }
      }
    ],
    idealFor: {
      da: "Ideelt for ejendomsejere, investorer og virksomheder, der ønsker at transformere ældre eller udtjente lokaler til moderne, attraktive rammer.",
      en: "Ideal for asset owners, investors, and firms wanting to transform dated premises into modern, attractive workplaces."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-totalrenovering-fredet-ejendom-koebenhavn.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-snedkerkoekken-specialinventar.jpg",
    faq: [
      {
        q: {
          da: "Hvordan griber CPH Collective et renoveringsprojekt an?",
          en: "How does CPH Collective approach a comprehensive renovation project?"
        },
        a: {
          da: "Vi indleder altid med en grundig forundersøgelse af den eksisterende bygningsmasse for at afdække konstruktionernes reelle tilstand og skjulte udfordringer. Herefter udarbejder vi en prioriteret handlingsplan med tilhørende budget og tidsplan, som afstemmes med bygherres ønsker. Under selve renoveringen styrer vores byggeledelse de forskellige håndværksfag i en nøje koordineret rækkefølge. Vi sikrer løbende kvalitetssikring og gennemskuelig kommunikation fra nedrivning til den færdige aflevering.",
          en: "We initiate every renovation with a meticulous preliminary survey of the existing building fabric to uncover structural realities and hidden constraints. Subsequently, we compile a prioritized execution plan accompanied by a transparent budget and milestone schedule aligned with client priorities. During active renovation, our site management orchestrates all building trades in a strictly synchronized sequence. We maintain continuous quality verification and transparent communication from initial soft strip-out through to final handover."
        }
      },
      {
        q: {
          da: "Hvad gør I, hvis der opstår uforudsete forhold i ældre bygninger?",
          en: "How do you manage unforeseen conditions in older buildings?"
        },
        a: {
          da: "Uforudsete forhold som råd, asbest eller skjulte rørføringer er en velkendt faktor ved renovering af ældre ejendomme i København. Når vi støder på uventede forhold, standser vi det berørte område og vurderer situationen med bygherre og relevante fagfolk. Vi fremlægger altid en konkret løsningsmodel med dertilhørende pris- og tidskonsekvenser, inden arbejdet genoptages. Vores store erfaring med ældre byggeri betyder, at vi hurtigt finder holdbare og fagligt forsvarlige løsninger.",
          en: "Unforeseen conditions such as timber rot, legacy asbestos, or concealed pipework are common factors when renovating historic properties in Copenhagen. When encountering unexpected site conditions, we isolate the affected area and evaluate the situation directly with the client and structural specialists. We always formulate an actionable solution accompanied by clear cost and schedule implications before proceeding with works. Our extensive experience with historic properties ensures we identify durable, code-compliant solutions swiftly."
        }
      },
      {
        q: {
          da: "Kan bygningen være i drift eller beboet under renoveringen?",
          en: "Can the building remain occupied or operational during renovation?"
        },
        a: {
          da: "Ja, vi har stor erfaring med at udføre renoveringer i bygninger, hvor der fortsat er kontorarbejde, butiksdrift eller beboelse. Vi etablerer tætte støvvægge, midlertidige adgangsveje og afskærmninger for at mindske støj- og støvgener for brugere og naboer. Desuden planlægger vi de mest støjende arbejder på tidspunkter, der generer mindst muligt.",
          en: "Yes, we possess substantial experience carrying out renovations within occupied properties where commercial tenancies, retail stores, or residential quarters remain active. We establish dust-tight partitions, temporary access corridors, and acoustic barriers to minimize noise and dust for occupants and neighbors. In addition, we schedule noisy demolitions or core drillings during off-peak hours to minimize disruption."
        }
      },
      {
        q: {
          da: "Rådgiver I om energioptimering og grønne forbedringer ved renovering?",
          en: "Do you advise on energy optimization and sustainable building upgrades?"
        },
        a: {
          da: "En renovering er det ideelle tidspunkt til at forbedre bygningens energimærke og nedbringe de fremtidige driftsomkostninger. Vi rådgiver om effektiv efterisolering af tag, ydervægge og etageadskillelser samt udskiftning til moderne lavenergivinduer. Vi kigger også på optimering af ventilation, belysning og varmeanlæg i samarbejde med vores tekniske samarbejdspartnere. Det giver både en mærkbar økonomisk besparelse og et markant forbedret indeklima i hverdagen.",
          en: "A renovation represents an optimal opportunity to upgrade a building's energy rating and reduce long-term operational expenditures. We advise on high-performance thermal insulation for roofs, exterior facades, and intermediate floors alongside upgrades to modern low-energy glazing. We also examine modern ventilation heat-recovery, LED lighting, and heating plant optimization alongside our mechanical partners. This delivers substantial energy savings as well as an improved indoor climate for occupants."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I respekten for bygningens oprindelige arkitektoniske detaljer?",
          en: "How do you preserve the building's authentic architectural details?"
        },
        a: {
          da: "Ved renovering af klassiske og fredede ejendomme arbejder vi med stor nænsomhed for bygningens historiske DNA og originale proportioner. Vores tømrere mestrer genskabelse af oprindelige stuklister, fyldningsdøre, gerigter og klassiske trægulve. Vi samarbejder med konservatorer og specialleverandører for at finde tidstypiske materialer og overfladebehandlinger. Resultatet er en renovering, der forener moderne komfort og nutidige krav med bygningens sjæl og historie.",
          en: "When renovating classic and listed heritage properties, we approach the building's historical DNA and original proportions with the utmost care. Our craftsmen specialize in replicating original moldings, paneled doors, architraves, and traditional wide-plank timber floors. We collaborate with conservation officers and specialty suppliers to source authentic, era-appropriate materials and lime finishes. The outcome is a sensitive renovation uniting contemporary comfort with historic architectural integrity."
        }
      }
    ]
  },
  {
    id: "totalrenovation",
    slug: {
      da: "totalrenovering",
      en: "total-renovation"
    },
    title: {
      da: "Totalrenovering",
      en: "Total Renovation"
    },
    heroHeading: {
      da: "Totalrenovering og renovering af hus med samlet ansvar",
      en: "Total House Renovation with Unified Responsibility"
    },
    subtitle: {
      da: "En totalrenovering kræver mere end dygtige håndværkere. Den kræver et klart projektgrundlag, sikker koordinering og en plan, som alle fag kan arbejde efter. Hos CPH Collective samler vi planlægning, projektledelse og udførelse, så du får ét fast kontaktpunkt gennem hele renoveringen.",
      en: "A complete home renovation demands more than skilled craftspeople. It requires a clear project foundation, dependable coordination, and a structured plan that every trade can align with. At CPH Collective, we combine planning, project management, and practical execution to provide you with one single point of contact throughout the entire renovation."
    },
    seoTitle: {
      da: "Totalrenovering i København | CPH Collective",
      en: "Total Renovation Copenhagen | CPH Collective"
    },
    seoDescription: {
      da: "Totalrenovering af ejendom og hus i København og på Sjælland. CPH Collective sikrer samlet styring med fast kontaktpunkt.",
      en: "Comprehensive total house renovation across Copenhagen and Zealand. CPH Collective combines detailed planning with expert carpentry."
    },
    seoKeywords: {
      da: [
        "totalrenovering",
        "renovering af hus",
        "totalrenovering hus",
        "totalrenovering af hus pris",
        "renovering København",
        "totalentreprise renovering",
        "tømrerarbejde renovering",
        "tilbud totalrenovering",
        "boligrenovering",
        "ombygning af hus",
        "byggefirma København",
        "byggefirma Sjælland"
      ],
      en: [
        "total renovation",
        "house renovation",
        "full house remodeling",
        "house renovation cost",
        "residential renovation Copenhagen",
        "turnkey renovation",
        "carpentry renovation",
        "renovation quote",
        "home makeover Denmark"
      ]
    },
    description: {
      da: "En totalrenovering kræver mere end dygtige håndværkere. Den kræver et klart projektgrundlag, sikker koordinering og en plan, som alle fag kan arbejde efter. Hos CPH Collective samler vi planlægning, projektledelse og udførelse, så du får ét fast kontaktpunkt gennem hele renoveringen.",
      en: "A total house renovation requires more than skilled craftspeople. It demands a clear project foundation, secure coordination, and a structured plan for all active trades. At CPH Collective, we integrate planning, project management, and execution with a single point of contact."
    },
    longDescription: {
      da: [
        "En totalrenovering kræver mere end dygtige håndværkere. Den kræver et klart projektgrundlag, sikker koordinering og en plan, som alle fag kan arbejde efter. Hos CPH Collective samler vi planlægning, projektledelse og udførelse, så du får ét fast kontaktpunkt gennem hele renoveringen.",
        "Ved en totalrenovering af hus bliver flere dele af boligen fornyet som en samlet opgave. Det kan omfatte nye rumforløb, lofter, vægge, træarbejde, inventar og koordinering af installationer. Omfanget bliver tilpasset bygningens stand, dine ønsker og de eksisterende konstruktioner."
      ],
      en: [
        "A total renovation requires more than skilled craftspeople. It requires a clear project foundation, dependable coordination, and a structured plan that all trades can work toward. At CPH Collective, we bring together planning, project management, and execution so you have one dedicated point of contact.",
        "During a total home renovation, multiple parts of the residence are renewed as a unified undertaking. This can encompass new room layouts, ceilings, walls, woodwork, custom cabinetry, and coordination of MEP installations. The scope is tailored to the building's condition, your aspirations, and existing structural parameters."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "scope-and-completeness",
          type: "checklist",
          tag: {
            da: "01 / Omfang & Helhed",
            en: "01 / Scope & Cohesion"
          },
          title: {
            da: "Hvad rummer en totalrenovering af hus?",
            en: "What Does a Total House Renovation Encompass?"
          },
          paragraphs: [
            {
              da: "Ved en totalrenovering af hus bliver flere dele af boligen fornyet som en samlet opgave. Det kan omfatte nye rumforløb, lofter, vægge, træarbejde, inventar og koordinering af installationer. Omfanget bliver tilpasset bygningens stand, dine ønsker og de eksisterende konstruktioner.",
              en: "During a total house renovation, multiple areas of the property are updated as an integrated undertaking. This may include reconfigured layouts, ceilings, partition walls, bespoke woodwork, cabinetry, and coordination of technical installations. The scope is tailored to the building's current condition, your priorities, and structural requirements."
            },
            {
              type: "rich",
              da: "Når du søger på totalrenovering hus, kan opgaven dække alt fra en gennemgribende indvendig renovering til ændringer af konstruktioner og facade. Vi afklarer derfor tidligt, hvad der skal bevares, udskiftes eller bygges om. Læs også om vores arbejde med {link:renovering}.",
              en: "When searching for total home renovation, the scope can range from a thorough interior overhaul to structural alterations and facade refurbishment. We therefore clarify early on what should be preserved, replaced, or reconstructed. Read also about our general {link:renovering} work."
            }
          ],
          bulletsLabel: {
            da: "Centrale elementer i en totalrenovering af hus",
            en: "Core elements of a complete home renovation"
          },
          bullets: {
            da: [
              "Planlægning af rum, materialer og praktiske løsninger",
              "Tømrerarbejde, specialinventar og præcis montage",
              "Koordinering af relevante faggrupper og leverancer",
              "Kvalitetssikring og løbende dialog gennem byggeforløbet"
            ],
            en: [
              "Spatial planning, material selection, and practical design solutions",
              "Master carpentry, bespoke cabinetry, and precise installation",
              "Interdisciplinary coordination of all relevant trade packages and supplies",
              "Quality assurance and ongoing transparent dialogue throughout the build"
            ]
          }
        },
        {
          id: "structure-and-phases",
          type: "dual-cards",
          tag: {
            da: "02 / Struktur, Planlægning & Koordinering",
            en: "02 / Structure, Planning & Coordination"
          },
          title: {
            da: "Sådan skaber vi overblik fra start",
            en: "How We Establish Clarity From the Outset"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Et godt resultat begynder med grundig afklaring. Vi gennemgår projektets formål, omfang og forventninger, før arbejdet bliver planlagt. Ved større opgaver kan vi håndtere forløbet som {link:totalentreprise}, hvor ansvar og koordinering bliver samlet hos os.",
              en: "A successful outcome begins with thorough initial scoping. We evaluate the project's purpose, scope, and expectations before work is mapped out. For comprehensive undertakings, we can manage the entire trajectory as a {link:totalentreprise}, consolidating all accountability and scheduling with us."
            }
          ],
          cards: [
            {
              level: "h3",
              tag: {
                da: "Fase 01 · Projektafklaring & Plan",
                en: "Phase 01 · Scoping & Planning"
              },
              title: {
                da: "Fra projektafklaring til en realistisk plan",
                en: "From Project Scoping to a Realistic Plan"
              },
              paragraphs: [
                {
                  da: "Vi ser på bygningens nuværende forhold, de ønskede ændringer og eventuelle afhængigheder mellem fagene. Derefter kan projektet opdeles i klare aktiviteter med en rækkefølge, som understøtter fremdriften. Det giver et bedre grundlag for både tidsplan, økonomi og praktisk udførelse.",
                  en: "We assess the building's current state, desired alterations, and crucial dependencies between active trades. The project is then divided into discrete work stages with an optimal sequence that sustains momentum, providing a resilient baseline for timeline, budget, and craftsmanship."
                }
              ],
              bullets: {
                da: [
                  "Afklaring af behov og forventet omfang",
                  "Gennemgang af tegninger og relevant dokumentation",
                  "Planlægning af faggrupper, materialer og leverancer",
                  "Udførelse, opfølgning og afsluttende kvalitetssikring"
                ],
                en: [
                  "Clarification of client needs and project scale",
                  "Review of architectural drawings and technical documentation",
                  "Detailed scheduling of trades, materials, and deliveries",
                  "Flawless execution, progress monitoring, and QA sign-off"
                ]
              }
            },
            {
              level: "h3",
              tag: {
                da: "Fase 02 · Styring & Faglighed",
                en: "Phase 02 · Coordination & Craft"
              },
              title: {
                da: "Koordinering gør en mærkbar forskel",
                en: "Coordination Makes a Tangible Difference"
              },
              paragraphs: [
                {
                  da: "Under en omfattende renovering arbejder flere fag ofte i de samme områder. Vores projektledelse skaber klare aftaler om adgang, rækkefølge og ansvar. Samtidig kan vores egen tømrervirksomhed udføre det nødvendige tømrerarbejde og specialtilpassede løsninger.",
                  en: "During a comprehensive renovation, multiple specialized trades frequently share the same physical work zones. Our project management establishes crystal-clear agreements regarding access, sequence, and accountability, while our in-house carpentry team executes all precision woodwork and custom solutions."
                }
              ],
              bullets: {
                da: [
                  "Klare aftaler om adgang, rækkefølge og ansvar mellem alle faggrupper",
                  "Egen tømrervirksomhed til præcist konstruktivt arbejde og aptering",
                  "Løbende dialog og styring af snitflader på pladsen",
                  "Specialtilpassede snedkerløsninger tilpasset husets arkitektur"
                ],
                en: [
                  "Clear agreements on access, order of operations, and trade responsibilities",
                  "In-house carpenters handling structural carpentry and architectural joinery",
                  "Continuous on-site dialogue minimizing interface friction between trades",
                  "Bespoke joinery and millwork calibrated to the property's architectural character"
                ]
              }
            }
          ]
        },
        {
          id: "pricing-and-budget",
          type: "intro-callout",
          tag: {
            da: "03 / Pris & Økonomisk Ramme",
            en: "03 / Pricing & Cost Framework"
          },
          title: {
            da: "Hvad afgør prisen på en totalrenovering af hus?",
            en: "What Determines the Cost of a Total House Renovation?"
          },
          paragraphs: [
            {
              da: "Når du søger efter totalrenovering af hus pris, møder du ofte brede prisintervaller. De kan give et første pejlemærke, men ikke et sikkert budget. Husets stand, adgangsforhold, materialevalg, installationer, konstruktive ændringer og den ønskede finish har betydning for den samlede økonomi.",
              en: "When researching the cost of a total house renovation, you often encounter broad indicative price intervals. While they provide an initial reference point, they do not constitute a dependable project budget. The home's condition, access logistics, material choices, MEP installations, structural modifications, and desired finish all fundamentally shape the overall expenditure."
            },
            {
              da: "Et tilbud på totalrenovering bør derfor bygge på en konkret gennemgang af projektet. Jo tydeligere omfang og materialevalg er beskrevet, desto bedre bliver beslutningsgrundlaget. Vi udarbejder tilbud ud fra opgavens faktiske behov frem for en standardpris, som sjældent passer til hele huset.",
              en: "A quote for a complete home renovation should therefore be based on a concrete, rigorous review of the property and specifications. The more clearly scope and materials are defined, the stronger the foundation for decision-making. We prepare proposals based on actual needs rather than generic flat rates that rarely fit the complexities of an entire residence."
            }
          ],
          calloutBox: {
            tag: {
              da: "Gennemskuelig Økonomi & Tilbudsgrundlag",
              en: "Transparent Budgeting & Tailored Quotation"
            },
            text: {
              da: "Et tilbud på totalrenovering bør bygge på en konkret gennemgang af projektet frem for en standardpris. Jo tydeligere omfang og materialevalg er beskrevet, desto stærkere og mere trygt bliver jeres beslutningsgrundlag.",
              en: "A comprehensive renovation proposal should be based on a thorough review of the actual property rather than a generic rate. The more precisely scope and materials are specified, the more robust and reassuring your investment framework becomes."
            }
          },
          stats: [
            {
              label: { da: "Husets stand & alder", en: "Condition & Age" },
              value: { da: "Grundig gennemgang", en: "Full inspection" }
            },
            {
              label: { da: "Materialevalg & finish", en: "Materials & Finish" },
              value: { da: "Tilpasset jeres ønsker", en: "Client-curated" }
            },
            {
              label: { da: "Pris & tilbudsgrundlag", en: "Pricing Model" },
              value: { da: "Konkret individuelt tilbud", en: "Custom fixed proposal" }
            }
          ]
        },
        {
          id: "nationwide-coverage",
          tag: {
            da: "04 / Geografi & Projekttyper",
            en: "04 / Geographic Scope & Project Types"
          },
          title: {
            da: "Totalrenovering fra København til resten af Danmark",
            en: "Total Renovation from Copenhagen to all of Denmark"
          },
          paragraphs: [
            {
              da: "CPH Collective har base i København og udfører projekter i hovedstaden, på Sjælland og i Nordsjælland. Vi kører også ud til opgaver i hele Danmark, når projektets størrelse eller specialiserede indhold kræver vores kompetencer inden for koordinering, tømrerarbejde og præcis montage.",
              en: "Headquartered in Copenhagen, CPH Collective executes projects across the capital, Zealand, and North Zealand. We also mobilize nationwide when a project's scale or specialized craftsmanship calls for our distinct competence in multi-trade coordination, timber construction, and precise fitting."
            },
            {
              type: "rich",
              da: "Vi samarbejder især med bygherrer, ejendomsejere, erhvervskunder og professionelle projektpartnere. Vi løser også udvalgte boligprojekter, hvor en renovering af hus stiller særlige krav til materialer, detaljer og helhed. Du kan se eksempler på vores tilgang under {link:projekter}.",
              en: "We collaborate primarily with developers, property owners, commercial enterprises, and professional project partners. We also take on select high-end residential assignments where house renovation places exceptional demands on materials, fine detailing, and overall integrity. Explore our past work under {link:projekter}."
            }
          ],
          badges: {
            da: [
              "København & Frederiksberg",
              "Nordsjælland",
              "Sjælland",
              "Hele Danmark (efter aftale)",
              "Totalrenovering af hus",
              "Gennemgribende ombygning",
              "Erhvervsejendomme & Boliger",
              "Specialinventar & Snedkeri",
              "Konstruktivt tømrerarbejde"
            ],
            en: [
              "Copenhagen & Frederiksberg",
              "North Zealand",
              "Zealand",
              "Nationwide (by agreement)",
              "Total house renovation",
              "Full structural remodeling",
              "Commercial & Residential estates",
              "Custom cabinetry & joinery",
              "Structural carpentry"
            ]
          }
        },
        {
          id: "ready-for-quote",
          type: "callout",
          title: {
            da: "Få et tilbud på totalrenovering",
            en: "Get a Quote for Your Total Renovation"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Overvejer du en gennemgribende renovering, hjælper vi med at afklare muligheder, ansvar og næste skridt. Fortæl os om ejendommen, dine ønsker og den forventede tidsramme. {link:kontakt} for en uforpligtende projektgennemgang og et tilbud baseret på opgavens omfang.",
              en: "Considering an extensive renovation? We assist with defining opportunities, governance, and next steps. Tell us about the property, your priorities, and expected timeline. {link:kontakt} for a no-obligation project consultation and an accurate quote based on your actual requirements."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Planlægning af rumforløb, materialer og praktiske løsninger",
        "Tømrerarbejde, specialinventar og præcis montage",
        "Koordinering af relevante faggrupper og leverancer",
        "Klar tidsplan, faste aftaler og ét kontaktpunkt",
        "Gennemgang af bygningens stand og eksisterende konstruktioner",
        "Afsluttende kvalitetssikring og ordentlig aflevering"
      ],
      en: [
        "Spatial planning, material curation, and practical architectural solutions",
        "Master carpentry, bespoke cabinetry, and precision installation",
        "Interdisciplinary trade coordination and material supply logistics",
        "Clear master schedule, firm agreements, and a single point of contact",
        "Survey of the property's condition and load-bearing structures",
        "Rigorous quality assurance and documented handover"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Projektafklaring", en: "Project Scoping" },
        text: {
          da: "Vi afklarer formål, omfang og forventninger, gennemgår tegninger og undersøger bygningens forhold.",
          en: "We clarify purpose, scope, and expectations, reviewing drawings and assessing building conditions."
        }
      },
      {
        step: "02",
        title: { da: "Realistisk Plan", en: "Realistic Plan" },
        text: {
          da: "Arbejdet opdeles i klare faser med en gennemtænkt rækkefølge, som sikrer fremdrift og overblik.",
          en: "Work is divided into clear phases with a deliberate sequence ensuring continuous momentum."
        }
      },
      {
        step: "03",
        title: { da: "Koordinering & Tømrerarbejde", en: "Coordination & Craft" },
        text: {
          da: "Styring af adgang, leverancer og fag, mens vores tømrerafdeling udfører konstruktioner og aptering.",
          en: "Managing site access, deliveries, and trades while our carpentry team executes build-out."
        }
      },
      {
        step: "04",
        title: { da: "Kvalitetssikring & Aflevering", en: "Quality Handover" },
        text: {
          da: "Grundig kontrol af alle overflader og installationer, opfølgning på udeståender og tryg aflevering.",
          en: "Meticulous inspection of all finishes and installations, addressing punch items and clean handover."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Ét samlet kontaktpunkt", en: "Single Point of Contact" },
        text: {
          da: "Du slipper for at koordinere mellem murer, maler, VVS, el og tømrer – vi tager det fulde ansvar.",
          en: "You avoid the stress of coordinating multiple subcontractors – we take complete responsibility."
        }
      },
      {
        title: { da: "Egen tømrervirksomhed", en: "In-House Carpentry" },
        text: {
          da: "Vores tømrere sikrer højeste præcision ved konstruktioner, lofter, træarbejde og inventar.",
          en: "Our master carpenters ensure maximum precision in structural woodwork, ceilings, and fittings."
        }
      },
      {
        title: { da: "Gennemskuelig økonomi", en: "Transparent Budget" },
        text: {
          da: "Fast og konkret tilbud baseret på grundig forundersøgelse frem for usikre standardpriser.",
          en: "Solid, tailored proposals based on actual site analysis rather than risky ballparks."
        }
      }
    ],
    idealFor: {
      da: "Boligejere, bygherrer og ejendomsinvestorer, der ønsker en gennemgribende renovering af hus eller ejendom med samlet ansvar og kompromisløst håndværk.",
      en: "Homeowners, developers, and property investors seeking an extensive residential or estate overhaul with turnkey accountability and uncompromising craft."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-arkitekttegnet-villa-traefacade.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-snedkerkoekken-specialinventar.jpg",
    faq: [
      {
        q: {
          da: "Hvad adskiller en totalrenovering fra en almindelig renovering?",
          en: "What distinguishes a total renovation from standard remodeling?"
        },
        a: {
          da: "Ved en totalrenovering strippes ejendommen eller lejemålet ofte helt ind til de rå bærende konstruktioner, hvorefter alt genopbygges fra bunden. Dette omfatter typisk udskiftning af samtlige tekniske installationer, etablering af nye rumfordelinger samt nye gulve, lofter og overflader. En almindelig renovering fokuserer derimod ofte på specifikke rum, facader eller afgrænsede bygningsdele. Totalrenoveringen giver bygherre en fuldstændig fornyet bygning, der i praksis svarer til nybyggeri indvendigt.",
          en: "During a total renovation, the property is stripped back to its raw load-bearing structural envelope before being reconstructed from scratch. This encompasses replacing all electrical, plumbing, and ventilation installations, creating revised floor layouts, and fitting brand-new finishes. Standard renovations, in contrast, typically focus on discrete areas, bathroom suites, or exterior facades. A total renovation provides the client with a completely rejuvenated building that essentially performs like modern construction internally."
        }
      },
      {
        q: {
          da: "Hvor lang tid tager en komplet totalrenovering typisk?",
          en: "How long does a complete total renovation typically take?"
        },
        a: {
          da: "Tidsrammen afhænger af ejendommens areal, kompleksitet samt omfanget af myndighedsbehandling og statiske ændringer. En gennemgribende totalrenovering af en større lejlighed eller villa tager typisk mellem 4 og 8 måneder fra byggestart. Er der tale om en hel etageejendom eller et erhvervsdomicil, strækker forløbet sig ofte over 8 til 18 måneder. Vi udarbejder en realistisk tidsplan med indlagte buffere inden byggestart, så I har et klart billede af indflytningsdatoen.",
          en: "Duration depends on square meterage, structural complexity, municipal approvals, and necessary static alterations. A comprehensive gut-renovation of a substantial apartment or private villa typically spans 4 to 8 months from on-site mobilization. For multi-story commercial properties or full residential complexes, timelines range from 8 to 18 months. We prepare a realistic schedule with dedicated contingency buffers prior to commencement, providing complete clarity on move-in dates."
        }
      },
      {
        q: {
          da: "Hvordan sikrer CPH Collective overholdelse af brand- og lydkrav ved totalrenovering?",
          en: "How do you ensure compliance with fire and acoustic standards?"
        },
        a: {
          da: "Totalrenoveringer udløser ofte skærpede krav i Bygningsreglementet til etageadskillelsers brandmodstandsevne og trinlydsdæmpning mellem lejemål. Vi projekterer og opbygger godkendte klasseopdelte konstruktioner med dokumenterede brand- og lydisolerende materialer. Efter endt udførelse leverer vi den lovpligtige dokumentation og attester til brug for ibrugtagningstilladelsen.",
          en: "Full building overhauls often activate stricter building code requirements for floor-ceiling acoustic insulation and structural fire resistance between distinct units. We engineer and install certified fire-rated floor assemblies incorporating acoustic membranes and sound-absorbent mineral cores. Upon completion, we provide all required statutory compliance certificates for the municipality's final certificate of occupancy."
        }
      },
      {
        q: {
          da: "Hvad er de økonomiske fordele ved at vælge en totalrenovering frem for etapevise udbedringer?",
          en: "What are the financial benefits of choosing a total renovation over phased updates?"
        },
        a: {
          da: "Ved at samle hele ombygningen i ét forløb spares betydelige omkostninger til gentagne byggepladsetableringer, stilladsleje og oprydninger. Håndværkerne kan arbejde rationelt i én ubrudt rækkefølge uden at skulle tage hensyn til nyligt færdiggjorte overflader. Samtidig minimeres risikoen for fremtidige skader, fordi alle rør og kabler fornyes samtidigt under de nye konstruktioner. Endelig resulterer en helstøbt totalrenovering i en markant højere værdistigning på ejendommen.",
          en: "Consolidating an entire overhaul into one continuous programme yields considerable cost efficiencies on recurring site set-up, scaffolding, and cleanup fees. Specialist trades operate in a rational, uninterrupted workflow without the risk of damaging recently completed adjacent rooms. Concurrently, future maintenance risks drop drastically because all concealed plumbing and wiring are renewed simultaneously. Ultimately, an integrated total renovation generates significantly superior long-term asset valuation."
        }
      },
      {
        q: {
          da: "Er I ansvarlige for indhentning af byggetilladelse ved totalrenovering?",
          en: "Are you responsible for acquiring building permits for total renovations?"
        },
        a: {
          da: "Ja, vi kan påtage os hele myndighedsbehandlingen og udarbejde nødvendige statiske beregninger, brandplaner og facadeansøgninger. Vi har stor erfaring med dialogen med de københavnske kommuner og ved præcis, hvilke krav der stilles til dokumentationen. Det sparer jer for tidsrøvende sagsgange og sikrer en smidig godkendelsesproces for byggeriet.",
          en: "Yes, we can oversee the entire statutory permitting process, including preparing structural engineering calculations, fire strategies, and facade alteration applications. We maintain extensive experience collaborating with municipal authorities across Greater Copenhagen and know their documentation criteria intimately. This relieves you of bureaucratic friction and facilitates a prompt, compliant approval process."
        }
      }
    ]
  },
  {
    id: "site-services",
    slug: {
      da: "byggepladsservice",
      en: "construction-site-services"
    },
    title: {
      da: "Byggepladsservice",
      en: "Construction Site Services"
    },
    heroHeading: {
      da: "Byggepladsservice med styr på jeres byggeprojekt",
      en: "Construction Site Services with Complete Project Control"
    },
    subtitle: {
      da: "CPH Collective leverer byggepladsservice til bygherrer, entreprenører og professionelle samarbejdspartnere. Vi hjælper med at få den aktive byggeplads til at fungere gennem praktisk udførelse, koordinering og montagearbejde, så jeres byggeprojekt kan bevæge sig frem med struktur og fokus på sikkerhed.",
      en: "CPH Collective delivers construction site services for clients, general contractors, and professional partners. We help active jobsites operate seamlessly through practical execution, coordination, and installation work—ensuring your construction project advances with structure and safety."
    },
    seoTitle: {
      da: "Byggepladsservice København | CPH Collective",
      en: "Construction Site Services | CPH Collective"
    },
    seoDescription: {
      da: "Professionel byggepladsservice i København og på Sjælland. CPH Collective leverer byggepladsledelse, montage og pladslogistik.",
      en: "Professional construction site services in Copenhagen and Zealand. CPH Collective delivers site management, logistics, and safety."
    },
    seoKeywords: {
      da: [
        "byggepladsservice",
        "byggepladsledelse",
        "professionel byggepladsservice",
        "byggepladsservice København",
        "byggepladsservice Sjælland",
        "pladslogistik",
        "montagearbejde",
        "pladssikkerhed",
        "aktiv byggeplads",
        "byggepladsdrift",
        "tilbud byggepladsservice",
        "byggeprojekt koordinering"
      ],
      en: [
        "construction site services",
        "site management",
        "site logistics Copenhagen",
        "jobsite operations",
        "installation work",
        "site safety",
        "construction logistics Zealand",
        "site services Denmark"
      ]
    },
    description: {
      da: "CPH Collective leverer byggepladsservice til bygherrer, entreprenører og professionelle samarbejdspartnere. Vi hjælper med at få den aktive byggeplads til at fungere gennem praktisk udførelse, koordinering og montagearbejde, så jeres byggeprojekt kan bevæge sig frem med struktur og fokus på sikkerhed.",
      en: "CPH Collective provides construction site services for clients, contractors, and partners, keeping active jobsites running efficiently through execution, coordination, and installation."
    },
    longDescription: {
      da: [
        "CPH Collective leverer byggepladsservice til bygherrer, entreprenører og professionelle samarbejdspartnere. Vi hjælper med at få den aktive byggeplads til at fungere gennem praktisk udførelse, koordinering og montagearbejde, så jeres byggeprojekt kan bevæge sig frem med struktur og fokus på sikkerhed.",
        "Byggepladsservice handler om at understøtte den daglige drift, når flere aktører arbejder tæt sammen. Vi tilpasser indsatsen til pladsens organisering, tidsplan og krav. Det giver en fleksibel ressource, som kan indgå i den eksisterende byggepladsledelse og løse aftalte opgaver i takt med projektets udvikling."
      ],
      en: [
        "CPH Collective provides construction site services for clients, general contractors, and professional partners. We help active jobsites operate seamlessly through practical execution, coordination, and installation work, ensuring your project advances with structure and safety.",
        "Construction site services are about supporting everyday operations when multiple trades collaborate closely. We adapt our contribution to the site organization, timeline, and regulations, providing a flexible resource that integrates into site management."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "professional-site-services",
          type: "checklist",
          tag: {
            da: "01 / Daglig Drift & Faglig Understøttelse",
            en: "01 / Daily Operations & Trade Support"
          },
          title: {
            da: "Hvad får I med professionel byggepladsservice",
            en: "What you gain with professional construction site services"
          },
          paragraphs: [
            {
              da: "Byggepladsservice handler om at understøtte den daglige drift, når flere aktører arbejder tæt sammen. Vi tilpasser indsatsen til pladsens organisering, tidsplan og krav. Det giver en fleksibel ressource, som kan indgå i den eksisterende byggepladsledelse og løse aftalte opgaver i takt med projektets udvikling.",
              en: "Construction site services focus on underpinning day-to-day operations when multiple trades work in close proximity. We tailor our efforts to the site organization, schedule, and safety mandates—providing an agile workforce that embeds within existing site leadership to resolve agreed tasks as the build progresses."
            },
            {
              type: "rich",
              da: "Projekt, byggeri og pladsdrift skal hænge sammen, når mange fag arbejder på samme sted. Derfor afklarer vi opgaverne med den ansvarlige byggeledelse og koordinerer med de relevante fag. I får en partner, som forstår, at selv en mindre forsinkelse kan påvirke den næste aktivitet på byggepladsen.",
              en: "Planning, construction, and site logistics must operate in synergy when multiple trades share the same perimeter. We clarify work packages directly with site superintendents and coordinate with all relevant crafts. You gain a partner who understands that even a slight delay can ripple into subsequent trades on site."
            }
          ],
          bulletsLabel: {
            da: "Centrale fokusområder i vores byggepladsservice",
            en: "Core focus areas in our construction site services"
          },
          bullets: {
            da: [
              "Koordinering med byggeledelse, faggrupper og underentreprenører",
              "Planlægning omkring adgangsveje, arbejdszoner og pladslogistik",
              "Praktisk byggepladsservice tilpasset byggeprojektets aktuelle fase",
              "Montagearbejde og specialopgaver med fokus på præcis udførelse",
              "Opmærksomhed på pladssikkerhed, projektkrav og kvalitetssikring"
            ],
            en: [
              "Coordination with site management, active trades, and subcontractors",
              "Strategic planning of access routes, designated work zones, and site logistics",
              "Practical on-site support tailored dynamically to the build's current phase",
              "Precision installation work and bespoke trade packages",
              "Uncompromising attention to jobsite safety, project regulations, and QA protocols"
            ]
          }
        },
        {
          id: "site-management-and-collaboration",
          type: "dual-cards",
          tag: {
            da: "02 / Ledelse, Logistik & Samarbejde",
            en: "02 / Site Management, Logistics & Collaboration"
          },
          cards: [
            {
              level: "h3",
              tag: {
                da: "Faglig Ledelse & Logistik",
                en: "Site Leadership & Logistics"
              },
              title: {
                da: "Byggepladsledelse der samler de mange tråde",
                en: "Site management bringing the threads together"
              },
              paragraphs: [
                {
                  da: "God byggepladsledelse skaber overblik over, hvem der gør hvad og hvornår. Vi bidrager med koordinering af faggrupper, planlægning af underentreprenører og pladslogistik, når det er en del af vores aftale. Samtidig holder vi fokus på adgangsforhold, sikkerhed og den samlede tidsplan for jeres projekt.",
                  en: "Effective site management provides total clarity on who does what and when. We assist with trade coordination, subcontractor scheduling, and site logistics within the agreed scope, maintaining unwavering vigilance on access pathways, jobsite safety, and the master project timeline."
                },
                {
                  type: "rich",
                  da: "Vores rolle aftales ud fra det konkrete behov. Vi kan fungere som udførende pladsservicepartner på en etableret byggeplads eller tage et bredere koordinerende ansvar. Når opgaven indgår i en {link:totalentreprise}, kan vi samle planlægning, udførelse og løbende kommunikation gennem ét kontaktpunkt.",
                  en: "Our exact remit is tailored to project requirements. We can serve as an executive site service partner on an established jobsite or shoulder broader coordination duties. When integrated into a {link:totalentreprise}, we unite scheduling, physical execution, and continuous dialogue under a single point of contact."
                }
              ],
              bullets: {
                da: [
                  "Koordinering af faggrupper og underentreprenører",
                  "Pladslogistik, adgangsforhold og sikkerhedsronding",
                  "Samlet styring ved integration i en {link:totalentreprise}"
                ],
                en: [
                  "Coordination of trade crews and subcontractors",
                  "Site logistics, clear access routes, and safety rounds",
                  "Unified accountability when combined with a {link:totalentreprise}"
                ]
              }
            },
            {
              level: "h3",
              tag: {
                da: "Tydelig Kommunikation & Snitflader",
                en: "Communication & Trade Interfaces"
              },
              title: {
                da: "Samarbejde uden unødige grænseflader",
                en: "Collaboration without trade friction"
              },
              paragraphs: [
                {
                  da: "På en travl byggeplads er tydelig kommunikation lige så vigtig som den praktiske indsats. Vi arbejder tæt sammen med bygherre, projektledere og øvrige udførende, så ændringer og afhængigheder bliver håndteret i den rette rækkefølge. Det mindsker uklarhed og giver bedre rammer for stabil fremdrift.",
                  en: "On a busy construction site, lucid communication is just as vital as physical effort. We collaborate closely with clients, project managers, and fellow trades to resolve revisions and scheduling dependencies in the correct sequence, minimizing friction and fostering predictable momentum."
                },
                {
                  type: "rich",
                  da: "Inden opstart gennemgår vi omfang, adgang, ansvar og forventet bemanding. Herefter kan indsatsen tilpasses, hvis tidsplanen eller pladsens behov ændrer sig. I ved dermed, hvilke opgaver vi løser, og hvordan vores arbejde hænger sammen med resten af jeres {link:byggeprojekt}.",
                  en: "Prior to mobilization, we align on scope, site access, responsibilities, and expected staffing levels. Activities can adapt smoothly if timelines or site conditions shift, giving you certainty about our remit and how it connects to the rest of your {link:byggeprojekt}."
                }
              ],
              bullets: {
                da: [
                  "Tæt dialog med bygherre, projektledelse og øvrige udførende",
                  "Proaktiv håndtering af ændringer og afhængigheder",
                  "Gennemgang af omfang, adgang og bemanding inden opstart"
                ],
                en: [
                  "Close dialogue with client, project managers, and executing trades",
                  "Proactive handling of revisions and trade dependencies",
                  "Pre-start scoping of access, responsibilities, and staffing"
                ]
              }
            }
          ]
        },
        {
          id: "montage-and-carpentry",
          type: "architectural-box",
          tag: {
            da: "03 / Montage & Tømrerarbejde",
            en: "03 / Installation & Carpentry"
          },
          title: {
            da: "Montagearbejde med blik for hele byggeprojektet",
            en: "Installation work with an eye for the complete project"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Montagearbejde på en aktiv byggeplads kræver præcision og respekt for de fag, der arbejder før og efter os. CPH Collective udfører specialiserede montageopgaver og {link:toemrerarbejde}, hvor materialer, mål og rækkefølge skal passe. Vi koordinerer udførelsen med projektets øvrige aktiviteter.",
              en: "Mounting and installation work on an active site demands pinpoint accuracy and respect for crafts working before and after us. CPH Collective executes specialized installation packages and {link:toemrerarbejde}, ensuring materials, tolerances, and sequencing harmonize seamlessly with overall site activity."
            },
            {
              type: "rich",
              da: "Det er relevant ved blandt andet erhvervsindretning, akustikløsninger, facadeopgaver og specialdesignede elementer. Har I brug for en fast udførende partner, kan I læse mere om vores {link:toemrerarbejde}. Leverancen bliver altid afgrænset efter tegninger, forholdene på pladsen og den aftalte kvalitet.",
              en: "This is crucial for commercial fit-outs, acoustic ceiling systems, exterior timber facades, and bespoke architectural features. If you require a dedicated craftsman partner, explore our {link:toemrerarbejde}. Deliverables are defined by technical drawings, real-world jobsite conditions, and quality standards."
            }
          ],
          badgesLabel: {
            da: "Relevante montage- og tømreropgaver",
            en: "Specialized installation & carpentry packages"
          },
          badges: {
            da: [
              "Erhvervsindretning",
              "Akustikløsninger",
              "Facadeopgaver",
              "Specialdesignede elementer",
              "Præcist tømrerarbejde"
            ],
            en: [
              "Commercial fit-outs",
              "Acoustic panel installations",
              "Exterior facade carpentry",
              "Custom architectural features",
              "Precision carpentry"
            ]
          }
        },
        {
          id: "nationwide-coverage",
          tag: {
            da: "04 / Geografi & Kapacitet",
            en: "04 / Geography & Capacity"
          },
          title: {
            da: "Byggepladsservice fra København til hele Danmark",
            en: "Construction site services from Copenhagen across Denmark"
          },
          paragraphs: [
            {
              da: "Vi har base i København og løser byggepladsservice på Sjælland med fokus på København, Roskilde og resten af regionen. Vi kører også til byggepladser i Nordsjælland. Ved det rette projekt arbejder vi i hele Danmark, så geografien kan afklares sammen med kapacitet, tidsplan og opgavens omfang.",
              en: "With headquarters in Copenhagen, we deliver construction site services across Zealand with particular focus on Copenhagen, Roskilde, and surrounding municipalities. We regularly mobilize to jobsites in North Zealand, and travel nationwide for fitting projects based on capacity and timeline."
            },
            {
              type: "rich",
              da: "Vores erfaring spænder fra pladsservice ved større logistikbyggeri på Sjælland til montage og specialarbejde i erhvervsmiljøer. Fælles for opgaverne er behovet for struktur, præcis udførelse og respekt for den eksisterende pladsorganisation. Det skaber et godt grundlag for samarbejdet gennem hele forløbet. Se eksempler på vores arbejde under {link:projekter}.",
              en: "Our track record ranges from extensive site logistics at major logistics hubs in Zealand to architectural installation in prestigious corporate offices. Common to all projects is the need for rigorous structure, craftsmanship, and deference to site management. Discover past deliveries in our {link:projekter}."
            }
          ],
          badges: {
            da: [
              "København & Omegn",
              "Roskilde & Midtsjælland",
              "Nordsjælland",
              "Logistikbyggeri på Sjælland",
              "Erhvervsmiljøer & Kontor",
              "Hele Danmark efter aftale"
            ],
            en: [
              "Greater Copenhagen",
              "Roskilde & Central Zealand",
              "North Zealand",
              "Logistics builds across Zealand",
              "Corporate & commercial fit-outs",
              "Nationwide by agreement"
            ]
          }
        },
        {
          id: "help-on-your-site",
          level: "h2",
          type: "callout",
          title: {
            da: "Skal vi hjælpe på jeres byggeplads",
            en: "Need support on your construction site?"
          },
          paragraphs: [
            {
              da: "Fortæl os om byggepladsen, projektfasen og de opgaver, I ønsker løst. Vi tager en uforpligtende gennemgang og vurderer, hvordan vores byggepladsservice bedst kan indgå i jeres organisation. Derefter udarbejder vi et tilbud baseret på behov, ansvar, tidsplan og det forventede omfang.",
              en: "Tell us about your jobsite, build phase, and the tasks you need addressed. We carry out a no-obligation review and assess how our site services integrate best into your team, providing a tailored estimate based on scope, responsibilities, and timeline."
            },
            {
              type: "rich",
              da: "Jo tidligere grænseflader og praktiske krav bliver afklaret, desto bedre kan indsatsen planlægges. {link:kontakt-os} for en dialog om byggepladsledelse, montagearbejde eller løbende service til jeres næste projekt. Vi sørger for en faglig og konkret afklaring, som tager udgangspunkt i virkeligheden på pladsen.",
              en: "The earlier interfaces and practical parameters are settled, the smoother the execution. {link:kontakt-os} for a dialogue on site leadership, installation packages, or ongoing support for your next build. We guarantee a concrete, trade-certified scoping grounded in jobsite reality."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Koordinering med byggeledelse, faggrupper og underentreprenører",
        "Etablering af byggepladshegn, adgangsveje, zoner og skiltning",
        "Støvvægge, midlertidige afskærmninger og overdækning",
        "Montagearbejde og specialiserede tømreropgaver",
        "Løbende pladsoprydning og kildesortering af byggeaffald",
        "Kvalitetssikring, pladssikkerhed og vinterforanstaltninger"
      ],
      en: [
        "Coordination with site superintendents and active trades",
        "Perimeter hoarding, turnstile access, and zone signage",
        "Dust containment barriers, weather hoardings, and sheeting",
        "Installation packages and specialized carpentry assignments",
        "Continuous site clearance and source-separated waste handling",
        "Safety compliance, winterization, and rigorous QA verification"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Pladsindretningsplan & Afklaring", en: "Site Logistics Mapping" },
        text: {
          da: "Vi afklarer opgaver, adgangsveje, arbejdszoner og pladslogistik i tæt dialog med byggeledelsen.",
          en: "We clarify tasks, access routes, work zones, and site logistics in close dialogue with site leadership."
        }
      },
      {
        step: "02",
        title: { da: "Mobilisering & Sikring", en: "Mobilization & Hoarding" },
        text: {
          da: "Hurtig etablering af hegn, porte, lys, støvvægge og velfærdsfaciliteter tilpasset pladsen.",
          en: "Rapid erection of fencing, gates, temporary lighting, dust barriers, and site amenities."
        }
      },
      {
        step: "03",
        title: { da: "Aktiv Drift & Montage", en: "Active Operations & Mounting" },
        text: {
          da: "Daglig tilstedeværelse, affaldshåndtering, montagearbejde, leverancestyring og sikkerhedsronding.",
          en: "Daily on-site presence, waste management, installation work, intake coordination, and safety inspections."
        }
      },
      {
        step: "04",
        title: { da: "Afvikling & Rydning", en: "Demobilization & Cleanup" },
        text: {
          da: "Struktureret demontering af midlertidige installationer og aflevering i ren stand.",
          en: "Systematic dismantling of temporary infrastructure and pristine site handover."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Styr på pladsen & sikkerhed", en: "Site Control & Safety" },
        text: { da: "Når adgangsforhold, logistik og sikkerhed er under kontrol, arbejder håndværkerne markant mere effektivt.", en: "When access, logistics, and site safety are orchestrated, all trades work markedly more productively." }
      },
      {
        title: { da: "Faglighed & montage", en: "Craftsmanship & Mounting" },
        text: { da: "Vi kombinerer praktisk byggepladsservice med specialiserede tømrer- og montagekompetencer.", en: "We combine practical jobsite operations with specialized carpentry and installation expertise." }
      },
      {
        title: { da: "Fleksibel ressource", en: "Agile Site Capacity" },
        text: { da: "Vi tilpasser indsatsen til pladsens organisering, tidsplan og skiftende faser i hele Danmark.", en: "We scale our deployment to the site organization, timeline, and shifting phases across Denmark." }
      }
    ],
    idealFor: {
      da: "Bygherrer, entreprenører og professionelle samarbejdspartnere, som ønsker stabil fremdrift, professionel koordinering og sikker drift på den aktive byggeplads.",
      en: "Property owners, general contractors, and project managers seeking stable progress, expert trade coordination, and orderly operations on active sites."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-byggepladsservice-sikkerhed-styring.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-byggeri-under-udfoerelse-koebenhavn.jpg",
    faq: [
      {
        q: {
          da: "Hvad dækker CPH Collectives byggepladsservice over?",
          en: "What is included in CPH Collective's construction site services?"
        },
        a: {
          da: "Vores byggepladsservice omfatter alt det praktiske arbejde, der får en professionel byggeplads til at fungere effektivt, sikkert og ryddeligt i hverdagen. Vi løser opgaver som pladsetablering, adgangskontrol, skurvognsdrift, midlertidige installationer og affaldssortering. Derudover varetager vi løbende tømreropgaver såsom afdækning, provisorisk lukning af facader, opsætning af sikkerhedsrækværk og støvvægge. Vi servicerer både totalentreprenører, bygherrer og fagentreprenører på store og mellemstore pladser.",
          en: "Our construction site services encompass all operational duties that keep a professional jobsite functioning efficiently, safely, and tidily on a daily basis. We manage compound establishment, access control, welfare cabin servicing, temporary utilities, and certified waste segregation. Additionally, we handle structural protection works such as weatherproofing, provisional facade closures, safety handrails, and dust isolation partitions. We serve general contractors, property developers, and specialist trade packages across commercial and residential sites."
        }
      },
      {
        q: {
          da: "Hvordan bidrager professionel byggepladsservice til projektets fremdrift?",
          en: "How do professional site services improve overall project momentum?"
        },
        a: {
          da: "Når byggepladsen holdes ren, velorganiseret og fri for affald, kan de faglige håndværkere arbejde uforstyrret og effektivt. Ryddelige adgangsveje og logisk placerede materialedepoter reducerer spildtid på intern transport og unødig ventetid. Desuden minimeres risikoen for arbejdsulykker og skader på leverede byggematerialer markant. Det giver en roligere byggeproces med færre flaskehalse og et overordnet bedre arbejdsmiljø for alle.",
          en: "When a jobsite remains clean, methodical, and free of construction debris, skilled craftsmen can work productively without interruptions. Clear transit routes and logically zoned material storage minimize transit bottlenecks and lost labor hours. Furthermore, accident risks and mechanical damage to freshly delivered building supplies decrease dramatically. This fosters a disciplined build programme with fewer scheduling delays and an enhanced working environment for all stakeholders."
        }
      },
      {
        q: {
          da: "Kan I levere byggepladsservice med kort varsel ved akut behov?",
          en: "Can you provide construction site services on short notice for urgent needs?"
        },
        a: {
          da: "Ja, vi råder over en mobil og fleksibel stab af servicemedarbejdere og tømrere, som hurtigt kan rykke ud i Storkøbenhavn og på Sjælland. Hvad enten der opstår behov for akut storminddækning, genetablering af adgangsveje eller ekstra mandskab til pladsoprydning, finder vi en løsning. Vi tilpasser vores tilstedeværelse løbende efter byggeriets aktuelle fase og intensitet.",
          en: "Yes, we maintain an agile team of site operatives and carpenters capable of swift mobilization across Greater Copenhagen and Zealand. Whether you face storm-proofing emergencies, urgent restoration of perimeter safety barriers, or surge personnel for site clearances, we respond rapidly. We adapt our on-site headcount dynamically according to your project's phase and daily intensity."
        }
      },
      {
        q: {
          da: "Hvilket udstyr og hvilke maskiner råder I over til pladsdrift?",
          en: "What machinery and plant equipment do you operate for site logistics?"
        },
        a: {
          da: "Vi medbringer professionelt materiel til alle former for pladsrengøring, herunder industristøvsugere, højtryksrensere og affaldshåndteringsudstyr. Vores medarbejdere er certificerede til betjening af gængse løfte- og transportmaskiner som teleskoplæssere, trucks og byggepladselevatorer. Vi kan også levere midlertidig byggestrøm, belysningsmaster, affugtere og varmeblæsere til udtørring af råhuset. Alt udstyr vedligeholdes og sikkerhedsgodkendes løbende efter gældende regler.",
          en: "We deploy industrial-grade equipment for site sanitation, including heavy-duty HEPA extractors, pressure washers, and waste sorting systems. Our operatives hold valid certifications for operating common plant machinery, including telehandlers, forklifts, and personnel hoists. We also supply and manage temporary site power distribution, LED lighting masts, industrial dehumidifiers, and drying heaters. All machinery undergoes regular statutory maintenance and safety verification in full compliance with Danish regulations."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I overholdelse af arbejdsmiljø- og sikkerhedskrav (APV/PSS)?",
          en: "How do you ensure compliance with health and safety regulations (PSS)?"
        },
        a: {
          da: "Sikkerheden på byggepladsen er altid vores højeste prioritet, og vi arbejder strengt i henhold til pladsens plan for sikkerhed og sundhed (PSS). Vores folk etablerer korrekte faldsikringer, afspærringer og belysning ved gang- og kørearealer for at forebygge ulykker. Vi foretager daglige sikkerhedsrondinger og rapporterer straks om eventuelle risikoforhold til byggeledelsen. Vores proaktive tilgang sikrer et trygt arbejdsmiljø, der lever op til Arbejdstilsynets skærpede retningslinjer.",
          en: "Safety on the jobsite is always our foremost priority, and we operate strictly within the framework of the site's Health and Safety Plan (PSS). Our crews install edge protection, stairwell barriers, and adequate illumination along all walkways to proactively prevent slip and fall accidents. We conduct daily site walk-arounds, immediately reporting any identified hazard to the site superintendent. Our proactive safety culture ensures a compliant environment aligned with Danish Working Environment Authority guidelines."
        }
      }
    ]
  },
  {
    id: "custom-projects",
    slug: {
      da: "specialprojekter",
      en: "custom-projects"
    },
    title: {
      da: "Specialprojekter & Montage",
      en: "Custom Projects & Bespoke Builds"
    },
    subtitle: {
      da: "Skræddersyet tømrerarbejde og specialiserede konstruktioner",
      en: "Bespoke carpentry and specialized structural installations"
    },
    seoTitle: {
      da: "Specialprojekter & Montage | CPH Collective",
      en: "Custom Projects & Builds | CPH Collective"
    },
    seoDescription: {
      da: "Skræddersyet tømrerarbejde, specialinventar og unikke byggeprojekter i København. Tæt samarbejde med arkitekter og bygherrer.",
      en: "Bespoke carpentry, custom furniture, and unique structural projects in Copenhagen. Close collaboration with architects and designers."
    },
    seoKeywords: {
      da: [
        "specialprojekter",
        "skræddersyede tømrerløsninger",
        "specialbygget konstruktion København",
        "specialinstallationer",
        "montering af kunst og skulpturer",
        "udstillingsbyggeri",
        "arkitektonisk inventar"
      ],
      en: [
        "custom projects",
        "bespoke carpentry solutions",
        "custom construction Copenhagen",
        "specialized installations",
        "sculpture installation contractors",
        "exhibition build",
        "architectural joinery"
      ]
    },
    description: {
      da: "Udover traditionelt tømrerarbejde tilbyder vi skræddersyede projektløsninger til kunder med unikke design- eller monteringskrav. Vi arbejder tæt sammen med designere, kunstnere, arkitekter og projektledere.",
      en: "In addition to traditional carpentry, we offer custom project solutions for clients with unique design or installation requirements. We work closely with designers, artists, architects, and project managers."
    },
    longDescription: {
      da: [
        "Udover traditionelt tømrerarbejde tilbyder vi skræddersyede projektløsninger til kunder med unikke design- eller monteringskrav. Vi arbejder tæt sammen med designere, kunstnere, arkitekter og projektledere for at sikre sikker, præcis og professionel montering af specialdesignede elementer.",
        "Fra markante skulptur- og kunstinstallationer i byrummet til eksklusivt specialinventar i prestigebyggerier omsætter vi kreative skitser til teknisk realiserbare og holdbare strukturer.",
        "Vores værksteds- og montageteam har stor erfaring med at håndtere utraditionelle materialekombinationer som træ, stål, messing og komposit, altid udført med kompromisløs finish og strukturel integritet."
      ],
      en: [
        "In addition to traditional carpentry, we engineer bespoke project solutions for clients with distinct design or installation requirements. We collaborate closely with designers, artists, architects, and project managers to ensure safe, exact, and professional execution of customized elements.",
        "From prominent sculpture and art installations in public plazas to bespoke millwork in landmark buildings, we transform creative concepts into technically viable, durable structures.",
        "Our workshop and on-site assembly team brings deep experience in handling unconventional material combinations such as hardwood, structural steel, brass, and architectural composites with impeccable finish and structural integrity."
      ]
    },
    includes: {
      da: [
        "Specialbyggede træ- og stålkonstruktioner",
        "Skulptur- og kunstinstallationer i offentlige og private rum",
        "Udstillings- og displaybyggeri til messer og gallerier",
        "Specialdesignet receptions- og mødeinventar",
        "Teknisk sparring om bygbarhed, statik og materialer",
        "Præcis montering og sikring på lokationen"
      ],
      en: [
        "Custom-built timber and architectural steel assemblies",
        "Sculpture and public art installations in private and municipal realms",
        "Exhibition and pavilion construction for cultural institutions",
        "Bespoke reception desks, boardroom furnishings, and built-ins",
        "Technical engineering consults on statics, fastening, and materials",
        "Precision on-site positioning, rigging, and secure anchoring"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Design & Teknisk Sparring", en: "Design & Constructability" },
        text: {
          da: "Vi analyserer skitsemateriale og finder den optimale konstruktive og håndværksmæssige metode.",
          en: "We review sketch concepts and identify the optimal structural engineering methodology."
        }
      },
      {
        step: "02",
        title: { da: "Prototype & Opmåling", en: "Prototyping & Survey" },
        text: {
          da: "Udarbejdelse af mock-ups, materialeprøver og 3D-opmåling af installationsstedet.",
          en: "Creating mock-ups, material testing, and exact 3D laser surveying of the site."
        }
      },
      {
        step: "03",
        title: { da: "Præfabrikation", en: "Workshop Prefabrication" },
        text: {
          da: "Elementerne fremstilles på værksted under kontrollerede forhold for maksimal præcision.",
          en: "Components are fabricated in controlled workshop conditions for maximum tolerance control."
        }
      },
      {
        step: "04",
        title: { da: "Montage & Overdragelse", en: "Rigging, Mount & Sign-off" },
        text: {
          da: "Specialiseret transport, kranløft og fejlfri montage på stedet med komplet dokumentation.",
          en: "Specialized transport, rigging, and flawless on-site installation with complete sign-off."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Kreativ ingeniørkunst", en: "Creative Engineering" },
        text: { da: "Vi elsker komplekse udfordringer og finder løsninger, hvor andre må give op.", en: "We embrace complex briefs and engineer viable solutions where others falter." }
      },
      {
        title: { da: "Strukturel sikkerhed", en: "Structural Integrity" },
        text: { da: "Alle specialinstallationer beregnes og sikres efter de højeste sikkerhedsstandarder.", en: "Every custom build is engineered and anchored to rigorous structural safety codes." }
      },
      {
        title: { da: "Kompromisløs finish", en: "Master Craftsman Touch" },
        text: { da: "Håndværk i særklasse, hvor hver detalje tåler det mest kritiske blik.", en: "Exceptional joinery craftsmanship where every join withstands meticulous scrutiny." }
      }
    ],
    idealFor: {
      da: "Arkitekter, kunstnere, kulturinstitutioner og ambitiøse virksomheder med visionære idéer, der kræver specialiseret håndværk.",
      en: "Architects, artists, cultural institutions, and ambitious brands with bold design concepts needing bespoke artisan execution."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-specialprojekt-arkitektonisk-traekonstruktion.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-snedkerarbejde-specialdesignet-traeinterioer.jpg",
    faq: [
      {
        q: {
          da: "Hvad kendetegner et specialprojekt hos CPH Collective?",
          en: "What defines a custom or bespoke project at CPH Collective?"
        },
        a: {
          da: "Specialprojekter er unikke opgaver, der falder uden for standardbyggeriets faste rammer og kræver ekstraordinær håndværksmæssig præcision. Det kan være arkitektoniske træskulpturer, skræddersyet specialinventar, avancerede akustiklofter eller integrerede rummøbler. Vi kombinerer avanceret træteknologi og snedkerpræcision med en dyb forståelse for arkitektens vision. Hvert projekt udvikles individuelt i et tæt partnerskab mellem bygherre, arkitekt og vores håndværkere.",
          en: "Custom projects encompass distinctive installations that diverge from standard commercial building formats and demand extraordinary joinery precision. Examples include architectural timber sculptures, bespoke integrated millwork, intricate acoustic ceilings, and built-in spatial elements. We synthesize sophisticated timber engineering and artisan cabinetry with a deep respect for the architect's creative vision. Every custom build is realized through tight, transparent collaboration between client, architectural designer, and our craftsmen."
        }
      },
      {
        q: {
          da: "Hvordan foregår samarbejdet med arkitekter og designere på specialopgaver?",
          en: "How do you collaborate with architects and designers on bespoke builds?"
        },
        a: {
          da: "Vi indtræder gerne tidligt i designfasen for at yde konstruktiv sparring om materialevalg, samlingsdetaljer og statiske udfordringer. Vi bygger ofte prototyper eller 1:1 mock-ups, så arkitekten og bygherren kan vurdere proportioner, finish og overflader i virkeligheden. Vores tømrere og snedkere tager stolthed i at finde elegante tekniske løsninger på komplekse formgivninger. Samarbejdet er præget af åbenhed, respekt for formsproget og fokus på det kompromisløse resultat.",
          en: "We eagerly engage early in the schematic design phase to provide constructive advice on species selection, structural joinery, and engineering feasibility. We frequently construct physical prototypes or 1:1 scale mock-ups so the design team can evaluate tactile finishes, joinery tolerances, and lighting interplay. Our master joiners take immense pride in devising elegant structural mechanisms for mathematically complex geometries. The collaboration is underpinned by openness, design fidelity, and a mutual commitment to uncompromising craftsmanship."
        }
      },
      {
        q: {
          da: "Hvilke materialer kan I kombinere med træ i specialprojekter?",
          en: "Which materials do you combine with timber in bespoke commissions?"
        },
        a: {
          da: "Selvom træ og snedkerfaglighed er vores kernekompetence, integrerer vi ofte elementer af stål, messing, glas, sten og integreret LED-belysning. Vi har et stærkt netværk af dygtige smede, glarmestre og stenhuggere, som vi koordinerer direkte med. Det sikrer knivskarpe overgange og tolerancer, hvor forskellige materialer mødes i det færdige design.",
          en: "While architectural woodwork and joinery represent our core discipline, we frequently incorporate bespoke metalwork, patinated brass, architectural glass, natural stone, and integrated LED illumination. We orchestrate directly with vetted precision fabricators, glaziers, and stone masons within our trade network. This ensures immaculate transition junctions and hairline tolerances wherever distinct materials intersect."
        }
      },
      {
        q: {
          da: "Udfører I specialprojekter til både private og erhvervskunder?",
          en: "Do you undertake custom projects for both private and corporate clients?"
        },
        a: {
          da: "Ja, vi leverer specialløsninger til såvel eksklusive private boliger som markante erhvervs- og kulturinstitutioner. Til erhverv har vi blandt andet fremstillet skulpturelle receptioner, skræddersyede mødeborde, udstillingspodier og akustiske vægbeklædninger. I private hjem skaber vi snedkererede køkkener, walk-in closets og skræddersyede trappekonstruktioner. Fællesnævneren er altid det høje ambitionsniveau og kravet om enestående detaljerigdom.",
          en: "Yes, we fabricate and install bespoke environments for luxury private residences as well as prestigious corporate and cultural institutions. For commercial clients, we have crafted sculptural reception portals, boardroom tables, gallery display plinths, and acoustic baffles. In high-end private residences, we build custom kitchens, library suites, walk-in wardrobes, and statement staircases. Across all commissions, the consistent hallmark is our exacting standard of finish and detail."
        }
      },
      {
        q: {
          da: "Hvor foregår produktionen af specialinventar og præfabrikerede dele?",
          en: "Where does manufacturing of custom furniture and joinery occur?"
        },
        a: {
          da: "Mindre komponenter og specialinventar præfabrikeres på vores samarbejdende snedkerværksteder under kontrollerede klimatiske forhold. Når delene er overfladebehandlet og kvalitetstestet, fragtes de til byggepladsen for præcis montage af vores egne montører. Denne arbejdsmetode minimerer montagetiden på byggepladsen og sikrer en overlegen finish på de færdige elementer.",
          en: "Intricate assemblies and custom joinery modules are precision-built in dedicated joinery workshops under strictly regulated humidity and climate controls. Once surfaces receive factory-applied finishes and undergo comprehensive QA checks, units are transported to site for surgical assembly by our own craftsmen. This prefabrication methodology dramatically reduces on-site disruption while ensuring unblemished surface finishes."
        }
      }
    ]
  },
  {
    id: "byggeprojekt",
    slug: {
      da: "byggeprojekt",
      en: "construction-projects"
    },
    title: {
      da: "Byggeprojekt",
      en: "Construction Projects"
    },
    heroHeading: {
      da: "Byggeprojekt med samlet styring og præcis udførelse",
      en: "Construction projects with unified management and precision execution"
    },
    subtitle: {
      da: "Byggeprojekt med samlet styring og præcis udførelse",
      en: "Construction projects with unified management and precision execution"
    },
    seoTitle: {
      da: "Byggeprojekt med Samlet Styring | CPH Collective",
      en: "Construction Projects | CPH Collective"
    },
    seoDescription: {
      da: "Styring og udførelse af byggeprojekt i København og på Sjælland. CPH Collective tilbyder planlægning, projektstyring og tømrerarbejde.",
      en: "Construction project management in Copenhagen and Zealand. CPH Collective provides planning, project management, and practical execution."
    },
    seoKeywords: {
      da: [
        "byggeprojekt",
        "hjælp til byggeprojektet",
        "planlægning af byggeprojekt",
        "styring af byggeprojekt",
        "erhvervsbyggeri",
        "nybyggeri",
        "totalentreprise",
        "tømrerarbejde",
        "byggepladsservice",
        "tilbud på byggeprojekt",
        "byggefirma København",
        "byggefirma Sjælland"
      ],
      en: [
        "construction project",
        "building project management",
        "construction planning",
        "commercial construction",
        "new building",
        "turnkey contracting",
        "carpentry",
        "site services",
        "construction quote",
        "Copenhagen",
        "Denmark"
      ]
    },
    description: {
      da: "Et byggeprojekt kræver mere end dygtige håndværkere. Det kræver klare beslutninger, realistiske rammer og koordinering mellem alle, der bidrager. Hos CPH Collective får I en bygge og tømrervirksomhed i København, som hjælper med planlægning, projektstyring og praktisk udførelse i hele Danmark.",
      en: "A construction project demands more than skilled tradespeople. It requires clear decisions, realistic frameworks, and coordination between all contributors. CPH Collective is a building and carpentry contractor in Copenhagen delivering planning, project management, and execution across Denmark."
    },
    longDescription: {
      da: [
        "Et byggeprojekt kræver mere end dygtige håndværkere. Det kræver klare beslutninger, realistiske rammer og koordinering mellem alle, der bidrager. Hos CPH Collective får I en bygge og tømrervirksomhed i København, som hjælper med planlægning, projektstyring og praktisk udførelse i hele Danmark.",
        "God projektledelse handler om at skabe overblik og tydelige beslutningsveje. Vi tilpasser vores rolle til opgavens størrelse, entrepriseform og eksisterende projektorganisation. Det giver et sammenhængende forløb, hvor bygherre, rådgivere, håndværkere og øvrige samarbejdspartnere arbejder efter fælles rammer."
      ],
      en: [
        "A construction project requires more than skilled craftspeople. It requires clear decisions, realistic parameters, and coordination among everyone who contributes. At CPH Collective, you get a building and carpentry company in Copenhagen providing planning, project management, and practical execution throughout Denmark.",
        "Good project management is about creating clarity and clear decision pathways. We adapt our role to the scale of the task, contract form, and existing project organization. This delivers a cohesive workflow where the client, consultants, craftspeople, and other partners operate within shared frameworks."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "early-clarification",
          level: "h2",
          title: {
            da: "Få hjælp til byggeprojektet fra første afklaring",
            en: "Get help with your building project from the initial scoping"
          },
          paragraphs: [
            {
              da: "Når I har brug for hjælp til byggeprojektet, starter vi med at afklare formål, omfang, tidsplan og faglige krav. Vi ser på, hvilke roller der skal samles, og hvor vi skaber mest værdi som totalentreprenør, udførende tømrer, montagepartner eller koordinator for de involverede faggrupper.",
              en: "When you need support with your construction project, we start by clarifying purpose, scope, timeline, and trade requirements. We assess which roles need aligning, and where we add maximum value as turnkey contractor, executive carpenter, installation partner, or trade coordinator."
            },
            {
              type: "rich",
              da: "En tidlig dialog gør det lettere at opdage afhængigheder, før de påvirker økonomi og fremdrift. Har projektet behov for et samlet ansvar, kan I læse mere om vores {link:totalentreprise}. Teknisk afklaring og {link:projektering} kan også indgå i forløbet.",
              en: "Early dialogue makes it easier to spot dependencies before they impact budgets or momentum. If the project requires unified accountability, learn more about our {link:totalentreprise}. Technical clarification and {link:projektering} can also be integrated into the process."
            }
          ]
        },
        {
          id: "structured-process",
          level: "h2",
          title: {
            da: "Fra projekt til byggeri med en gennemarbejdet proces",
            en: "From design to construction through a thoroughly refined process"
          },
          paragraphs: [
            {
              da: "God projektledelse handler om at skabe overblik og tydelige beslutningsveje. Vi tilpasser vores rolle til opgavens størrelse, entrepriseform og eksisterende projektorganisation. Det giver et sammenhængende forløb, hvor bygherre, rådgivere, håndværkere og øvrige samarbejdspartnere arbejder efter fælles rammer.",
              en: "Good project management is about creating transparency and distinct channels for decisions. We tailor our involvement to the job size, contract model, and existing project team. This creates a cohesive timeline where clients, advisors, and trades work toward shared milestones."
            }
          ]
        },
        {
          id: "planning-and-coordination",
          type: "dual-cards",
          cards: [
            {
              level: "h3",
              tag: { da: "Fase 01 · Forberedelse", en: "Phase 01 · Preparation" },
              title: {
                da: "Planlægning af byggeprojekt med klare rammer",
                en: "Construction planning with clear frameworks"
              },
              intro: {
                da: "Ved planlægning af byggeprojekt gennemgår vi de praktiske og faglige forhold, som har betydning for udførelsen. Det kan være adgangsforhold, materialer, grænseflader mellem fag og rækkefølgen på arbejdet. Målet er at skabe et realistisk grundlag, før aktiviteterne på byggepladsen begynder.",
                en: "During building project planning, we review the practical and technical conditions affecting execution. These include site access, materials, trade interfaces, and sequencing. The objective is establishing a realistic foundation before site operations commence."
              },
              bullets: {
                da: [
                  "Afklaring af behov, funktioner og projektets omfang",
                  "Vurdering af tidsplan, faggrupper og praktiske forhold",
                  "Fastlæggelse af ansvar, kommunikation og næste beslutning"
                ],
                en: [
                  "Clarification of requirements, functions, and project scope",
                  "Assessment of schedule, trade requirements, and site logistics",
                  "Definition of responsibilities, communication lines, and milestones"
                ]
              }
            },
            {
              level: "h3",
              tag: { da: "Fase 02 · Udførelse", en: "Phase 02 · Execution" },
              title: {
                da: "Koordinering mellem fag og byggeplads",
                en: "Coordination between trades and the jobsite"
              },
              intro: {
                type: "rich",
                da: "Når udførelsen går i gang, følger vi op på aftaler, fremdrift og kvalitet. Vi kan koordinere faggrupper og underentreprenører samt håndtere {link:toemrerarbejde}, montage og relevante dele af pladsdriften. Ved aktive byggepladser kan vores {link:byggepladsservice} understøtte struktur, sikkerhed og logistik.",
                en: "Once site work commences, we follow up diligently on agreements, timeline, and craftsmanship. We coordinate subcontractors and active trades while handling {link:toemrerarbejde}, installations, and key aspects of site operations. On active sites, our {link:byggepladsservice} bolsters structure, safety, and logistics."
              },
              bullets: {
                da: [
                  "Løbende koordinering med bygherre og samarbejdspartnere",
                  "Opfølgning på aktiviteter, tidsplan og aftalte leverancer",
                  "Kvalitetssikring af vores udførte arbejde",
                  "Praktisk hensyn til adgang, sikkerhed og andre fag"
                ],
                en: [
                  "Continuous coordination with client and project partners",
                  "Close monitoring of work packages, timeline, and agreed deliverables",
                  "Quality assurance and verification of executed craft",
                  "Practical consideration for access, safety protocols, and adjacent trades"
                ]
              }
            }
          ]
        },
        {
          id: "handover-and-completion",
          level: "h3",
          title: {
            da: "Afslutning og aflevering med styr på detaljerne",
            en: "Completion and handover with focus on detail"
          },
          paragraphs: [
            {
              da: "Den sidste del af et byggeprojekt kræver samme opmærksomhed som opstarten. Vi gennemgår vores leverancer, følger op på eventuelle udeståender og bidrager til en ordentlig afslutning. Det skaber bedre overblik ved aflevering og gør det tydeligt, hvad der er udført inden for vores ansvarsområde.",
              en: "The concluding chapter of a building project demands the same vigilance as mobilization. We thoroughly inspect our deliverables, resolve punch-list items, and support an orderly handover. This provides absolute clarity at sign-off, confirming every task executed within our remit."
            }
          ]
        },
        {
          id: "nationwide-coverage",
          level: "h2",
          title: {
            da: "Erhvervsbyggeri og nybyggeri fra København til hele Danmark",
            en: "Commercial construction and new builds from Copenhagen across Denmark"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Vi arbejder med erhvervsbyggeri, nybyggeri, {link:renovering}, indretning og specialtilpassede konstruktioner. Opgaverne kan omfatte kontorer, erhvervslokaler, pavilloner, logistikbyggeri og arkitektoniske elementer. Vi kan kombinere styring med professionelt tømrerarbejde, snedkerarbejde og præcis montage.",
              en: "We handle commercial buildings, new construction, {link:renovering}, fit-outs, and bespoke structural solutions. Projects span offices, retail environments, pavilions, logistics facilities, and architectural features. We blend disciplined management with skilled carpentry, joinery, and precise mounting."
            },
            {
              type: "rich",
              da: "CPH Collective har base i København og udfører byggeprojekter på Sjælland og i Nordsjælland. Vi kører også ud til opgaver i resten af Danmark, når projektets karakter og omfang kræver vores kompetencer. Se eksempler på forskellige leverancer under {link:projekter}.",
              en: "CPH Collective is headquartered in Copenhagen, executing construction projects across Greater Copenhagen and North Zealand. We also travel throughout Denmark when a project demands our expertise. Explore examples of past deliverables in our {link:projekter}."
            }
          ]
        },
        {
          id: "quote-and-consultation",
          level: "h2",
          type: "callout",
          title: {
            da: "Få et tilbud på byggeprojektet",
            en: "Request a quote for your building project"
          },
          paragraphs: [
            {
              da: "Et tilbud på byggeprojektet tager udgangspunkt i den konkrete opgave. Pris og tidsramme afhænger blandt andet af projektets størrelse, materialevalg, adgangsforhold, faglige grænseflader og den ønskede entrepriseform. Tegninger, beskrivelser og en ønsket tidsplan giver os et bedre grundlag for vurderingen.",
              en: "A quote for a construction project is tailored to the specific assignment. Cost and schedule hinge on project size, material choices, site access, trade interfaces, and preferred contract form. Drawings, specifications, and a desired schedule provide us with the best basis for assessment."
            },
            {
              type: "rich",
              da: "Har I et kommende projekt, byggeri eller en udfordring på en aktiv byggeplads, tager vi gerne en indledende samtale om behovet. {link:kontakt} for en uforpligtende projektgennemgang og et tilbud baseret på opgavens reelle omfang.",
              en: "If you have an upcoming project, build, or a challenge on an active site, we welcome an initial dialogue. {link:kontakt} for a non-binding project review and a proposal rooted in your actual project scope."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Projektstyring, tidsplanlægning og budgetopfølgning",
        "Koordinering af underentreprenører og faggrupper",
        "Udførende tømrerarbejde og montage",
        "Løbende kvalitetssikring og KS-dokumentation",
        "Praktiske foranstaltninger, adgangsforhold og logistik",
        "Grundig aflevering, gennemgang og opfølgning"
      ],
      en: [
        "Project management, scheduling, and budget tracking",
        "Subcontractor coordination and trade management",
        "Carpentry execution and precision mounting",
        "Continuous quality control and QA documentation",
        "Logistical planning, site access, and jobsite safety",
        "Rigorous handover, inspection, and post-completion follow-up"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Første Afklaring & Rammer", en: "Scoping & Baseline" },
        text: {
          da: "Afklaring af formål, omfang, tidsplan og faglige krav. Vi fastlægger roller, snitflader og samarbejdsmodel.",
          en: "Clarifying purpose, scope, timeline, and trade requirements to define roles and collaboration model."
        }
      },
      {
        step: "02",
        title: { da: "Planlægning & Logistik", en: "Planning & Logistics" },
        text: {
          da: "Gennemgang af adgangsforhold, materialer, grænseflader mellem fag og rækkefølge før pladsstart.",
          en: "Review of site access, materials, inter-trade interfaces, and sequencing prior to mobilization."
        }
      },
      {
        step: "03",
        title: { da: "Udførelse & Koordinering", en: "Execution & Coordination" },
        text: {
          da: "Styring af fremdrift, tømrerleverancer, pladsservice og proaktiv løsning af uforudsete forhold.",
          en: "Active oversight of timeline, carpentry deliveries, site services, and proactive problem-solving."
        }
      },
      {
        step: "04",
        title: { da: "Aflevering med Styr på Detaljen", en: "Detailed Handover" },
        text: {
          da: "Gennemgang af leverancer, opfølgning på eventuelle udeståender og en ordentlig, dokumenteret afslutning.",
          en: "Verification of deliverables, resolution of outstanding items, and clean documented handover."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Samlet styring & ansvar", en: "Unified Management" },
        text: {
          da: "Vi skaber overblik og tydelige beslutningsveje for bygherre, rådgivere og faggrupper.",
          en: "We create clarity and clear decision routes for clients, consultants, and contractors."
        }
      },
      {
        title: { da: "Faglighed i udførelsen", en: "Trade Craftsmanship" },
        text: {
          da: "Vores egen praktiske tømrererfaring sikrer, at løsningerne altid fungerer på byggepladsen.",
          en: "Our hands-on carpentry background ensures designs translate flawlessly onto the jobsite."
        }
      },
      {
        title: { da: "Dækker hele Danmark", en: "Nationwide Reach" },
        text: {
          da: "Med base i København løser vi opgaver i hele hovedstaden, Sjælland og resten af landet.",
          en: "Headquartered in Copenhagen, we deliver projects across Greater Copenhagen, Zealand, and all Denmark."
        }
      }
    ],
    idealFor: {
      da: "Bygherrer, rådgivere og erhvervsvirksomheder, der ønsker én kompetent samarbejdspartner til styring, koordinering og udførelse af byggeprojekter.",
      en: "Developers, consultants, and commercial enterprises seeking an accountable partner for building management, coordination, and craft execution."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-byggeri-under-udfoerelse-koebenhavn.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-byggepladsservice-sikkerhed-styring.jpg",
    faq: [
      {
        q: {
          da: "Hvad kan CPH Collective tilbyde i et samlet byggeprojekt?",
          en: "What does CPH Collective offer for an integrated construction project?"
        },
        a: {
          da: "Vi tilbyder en helhedsorienteret styring og udførelse, der favner projektet fra den indledende idé til den endelige ibrugtagning. Vi hjælper med at definere projektets rammer, udarbejde byggeprogram og sammensætte det rette hold af rådgivere og håndværkere. Under byggeriet varetager vi byggeledelsen, økonomistyringen og sikrer, at kvalitetsstandarder overholdes på alle niveauer. Vores fleksible model kan tilpasses både mindre ombygninger og større nybyggerier på Sjælland.",
          en: "We offer holistic management and craft execution spanning your building project from early concept definition to formal handover. We assist in scoping project parameters, formulating building programs, and assembling an optimal team of technical specialists and trades. Throughout active construction, we manage daily site superintendence, cost governance, and strict quality compliance across every trade package. Our adaptive model scales effortlessly to private residential projects as well as complex commercial developments."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I gennemsigtighed i økonomi og tidsplan for byggeprojektet?",
          en: "How do you guarantee financial and schedule transparency throughout the build?"
        },
        a: {
          da: "Vi tror på fuld åbenhed og benytter transparente kalkulationer, hvor alle poster og fagentrepriser fremgår tydeligt. Bygherre modtager faste månedsrapporter med opdateret økonomi, likviditetsflow og en detaljeret vurdering af tidsplanen. Viser der sig ændringsbehov undervejs, fremlægges en klar konsekvensberegning, før der træffes beslutninger. Det giver et trygt beslutningsgrundlag, hvor bygherre bevarer fuld kontrol over sine investeringer.",
          en: "We believe in unwavering transparency, utilizing itemized cost estimates where every line item and trade deliverable is fully visible. The client receives scheduled monthly progress reports detailing expenditure against budget, cash flow projections, and timeline milestones. When design amendments or variations emerge, we prepare explicit impact calculations before commitments are approved. This provides a secure decision framework that empowers clients to retain complete authority over capital expenditures."
        }
      },
      {
        q: {
          da: "Hvem koordinerer de forskellige faggrupper under byggeprojektet?",
          en: "Who coordinates the various specialist trades on the construction project?"
        },
        a: {
          da: "Det gør CPH Collectives erfarne byggeleder, som fungerer som det centrale bindeled mellem murere, elektrikere, vvs'ere, tømrere og malere. Byggelederen planlægger de daglige grænseflader og sikrer, at ingen faggrupper spilder tid på at vente på hinanden. Vi afholder ugentlige pladsmøder og følger løbende op på sikkerhed, rækkefølge og fremdrift.",
          en: "Coordination is handled by CPH Collective's dedicated site manager, who serves as the operational link between masons, electricians, plumbers, carpenters, and decorators. The site manager orchestrates daily interfaces, guaranteeing that no contractor loses productive time waiting for preceding trades to vacate work areas. We host weekly site progress meetings and continuously monitor safety, trade sequencing, and productivity."
        }
      },
      {
        q: {
          da: "Hvad er bygherres egen rolle undervejs i et byggeprojekt med jer?",
          en: "What is the client's role during a construction project with CPH Collective?"
        },
        a: {
          da: "Bygherres primære rolle er at træffe de overordnede strategiske og æstetiske valg ud fra de beslutningsoplæg, vi fremlægger. I slipper for at håndtere tekniske grænsefladeproblemer, mandskabslogistik og daglige byggetvister. Vi holder faste statusmøder, hvor vi præsenterer prøver, budgetstatus og afklarer eventuelle ønsker til materialer. I forbliver den styrende part, mens vi tager det tunge slæb i den daglige byggeledelse.",
          en: "The client's primary responsibility is making strategic and architectural design decisions based on organized briefings we prepare. You are liberated from resolving technical interface conflicts, labor disputes, and intricate logistical challenges. We organize structured status reviews to present material mock-ups, budget tracking, and evaluate refinement options. You maintain governance over the project while we shoulder the operational weight of daily site delivery."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I en problemfri aflevering og ibrugtagning af byggeriet?",
          en: "How do you ensure seamless handover and occupancy of the completed building?"
        },
        a: {
          da: "En vellykket aflevering planlægges længe før den sidste skrue er sat i væggen. Vi foretager systematiske før-gennemgange for at udbedre eventuelle skønhedsfejl, inden bygherre inviteres til den formelle aflevering. Ved overdragelsen udleverer vi en komplet digital drift- og vedligeholdelsesmappe (D&V) samt alle relevante godkendelser. Vi følger desuden op efter indflytning for at sikre, at bygningen fungerer perfekt i den daglige brug.",
          en: "A smooth project handover is planned months before the final architectural fittings are mounted. We conduct systematic snagging walks to resolve any aesthetic deficiencies before convening the formal client inspection. At handover, we provide a complete digital Operations and Maintenance (O&M) compendium alongside all regulatory certificates. We also follow up after occupancy to ensure the building functions impeccably in everyday operation."
        }
      }
    ]
  },
  {
    id: "haandvaerker-byggeprojekt",
    slug: {
      da: "haandvaerker-byggeprojekt",
      en: "trades-construction-projects"
    },
    title: {
      da: "Håndværker til byggeprojekt",
      en: "Craftsmen for Construction Projects"
    },
    heroHeading: {
      da: "Håndværker til byggeprojekt i København og på Sjælland",
      en: "Craftsmen for construction projects in Copenhagen and Zealand"
    },
    subtitle: {
      da: "Håndværker til byggeprojekt i København og på Sjælland",
      en: "Craftsmen for construction projects in Copenhagen and Zealand"
    },
    seoTitle: {
      da: "Håndværker til Byggeprojekt | CPH Collective",
      en: "Craftsmen for Projects | CPH Collective"
    },
    seoDescription: {
      da: "Håndværker til byggeprojekt i København og på Sjælland. CPH Collective kombinerer tømrerfaglig udførelse med professionel koordinering.",
      en: "Skilled craftsmen for construction projects in Copenhagen and Zealand. CPH Collective combines expert carpentry with reliable coordination."
    },
    seoKeywords: {
      da: [
        "håndværker til byggeprojekt",
        "håndværker hjælp",
        "håndværker tilbud",
        "tømrer til byggeprojekt",
        "håndværker København",
        "håndværker Sjælland",
        "håndværker Nordsjælland",
        "hjælp til byggeprojekt",
        "byggefirma København",
        "koordinering af håndværkere",
        "faglig håndværker",
        "tømrervirksomhed København"
      ],
      en: [
        "craftsmen construction project",
        "trades for building projects",
        "contractor quote copenhagen",
        "carpentry contractors zealand",
        "construction trade coordination",
        "tradesmen copenhagen",
        "commercial carpentry copenhagen"
      ]
    },
    description: {
      da: "Har I brug for en håndværker, der kan tage ansvar og indgå professionelt i jeres projektorganisation? Hos CPH Collective får I faglig håndværker hjælp til både planlagte og aktive byggeprojekter. Vi kombinerer tømrerfaglig udførelse med koordinering, overblik og klar kommunikation.",
      en: "Do you need a craftsman who takes accountability and integrates seamlessly into your project organization? At CPH Collective, you receive professional trade support for planned and active construction projects, blending carpentry craftsmanship with coordination, clarity, and communication."
    },
    longDescription: {
      da: [
        "Har I brug for en håndværker, der kan tage ansvar og indgå professionelt i jeres projektorganisation? Hos CPH Collective får I faglig håndværker hjælp til både planlagte og aktive byggeprojekter. Vi kombinerer tømrerfaglig udførelse med koordinering, overblik og klar kommunikation.",
        "Et byggeprojekt bliver hurtigt komplekst, når flere faggrupper, leverancer og deadlines skal hænge sammen. Vi sætter os ind i projektets rammer og den aftalte opgave, så vores arbejde passer ind i tidsplanen. Det giver færre uklare grænseflader og et bedre samarbejde på byggepladsen."
      ],
      en: [
        "Do you need a craftsman who takes accountability and integrates seamlessly into your project organization? At CPH Collective, you receive professional trade support for planned and active construction projects, blending carpentry craftsmanship with coordination, clarity, and communication.",
        "A construction project quickly turns complex when multiple trades, deliveries, and milestones must align. We immerse ourselves in the project parameters and agreed tasks so our work integrates cleanly into the schedule, reducing ambiguous interfaces and elevating site collaboration."
      ]
    },
    customEditorial: {
      sections: [
        {
          id: "holistic-understanding",
          type: "intro-callout",
          tag: { da: "01 / Forståelse for Helheden", en: "01 / Holistic Understanding" },
          title: {
            da: "Få en håndværker, der forstår hele byggeprojektet",
            en: "Engage craftsmen who understand the entire building project"
          },
          paragraphs: [
            {
              da: "Et byggeprojekt bliver hurtigt komplekst, når flere faggrupper, leverancer og deadlines skal hænge sammen. Vi sætter os ind i projektets rammer og den aftalte opgave, så vores arbejde passer ind i tidsplanen. Det giver færre uklare grænseflader og et bedre samarbejde på byggepladsen.",
              en: "A construction project quickly turns complex when multiple trades, deliveries, and milestones must align. We immerse ourselves in the project parameters and agreed tasks so our work integrates cleanly into the schedule, reducing ambiguous interfaces and elevating site collaboration."
            },
            {
              type: "rich",
              da: "Vi kan indgå som udførende håndværker på en afgrænset entreprise eller tage et bredere ansvar for koordinering og udførelse. Har I behov for at samle flere dele af projektet ét sted, kan I også læse om vores {link:totalentreprise} og vores arbejde med større {link:byggeprojekt}er.",
              en: "We can participate as executing craftsmen on a targeted package or shoulder broader coordination and execution responsibilities. If you need to consolidate multiple phases in one place, explore our {link:totalentreprise} and our work with larger {link:byggeprojekt}s."
            }
          ],
          calloutBox: {
            tag: { da: "Gennemskueligt Tilbudsgrundlag", en: "Transparent Basis for Quotes" },
            text: {
              da: "Når I indhenter et håndværker tilbud, bør grundlaget være tydeligt. Vi ser derfor på projektmateriale, adgangsforhold, tidsplan, materialer og grænseflader, før vi vurderer opgaven. Tilbuddet bliver baseret på det konkrete omfang frem for en standardløsning, der ikke tager højde for pladsens forhold.",
              en: "When requesting a contractor quote, the foundation must be transparent. We evaluate project documentation, site access, schedule, materials, and interfaces before assessing the work. Our proposal is grounded in your actual scope rather than an off-the-shelf estimate that ignores real site realities."
            }
          },
          stats: [
            { label: { da: "Rolle & Snitflader", en: "Role & Interfaces" }, value: { da: "Klart afstemt", en: "Clearly defined" } },
            { label: { da: "Udførelse", en: "Execution" }, value: { da: "Tømrerfaglig kvalitet", en: "Artisan carpentry" } },
            { label: { da: "Samarbejde", en: "Collaboration" }, value: { da: "Projekt & Byggeledelse", en: "Project management" } }
          ]
        },
        {
          id: "trade-deliverables",
          type: "feature-grid",
          tag: { da: "02 / Kerneleverancer", en: "02 / Core Deliverables" },
          title: {
            da: "Hvilke opgaver løser vi på byggeprojektet?",
            en: "What scopes do we deliver on the construction project?"
          },
          paragraphs: [
            {
              da: "Som bygge- og tømrervirksomhed kan vi tilknyttes på forskellige stadier af byggeprocessen og levere målrettet kapacitet til både nybyggeri, ombygning, renovering og montage.",
              en: "As a construction and carpentry company, we deploy across diverse phases of the build cycle, delivering dedicated capacity for new builds, fit-outs, renovations, and assembly."
            }
          ],
          cards: [
            {
              number: "01",
              tag: { da: "Rådgivning & Start", en: "Scoping & Kickoff" },
              title: { da: "Hjælp til byggeprojekt", en: "Project support" },
              description: {
                da: "Hjælp til byggeprojekt fra indledende afklaring og gennemgang af projektmateriale til praktisk udførelse på byggepladsen.",
                en: "Support throughout the building project from initial scoping and drawing review to hands-on site execution."
              },
              bullet: { da: "Fælles grundlag og afstemte forventninger", en: "Aligned foundation and clear expectations" }
            },
            {
              number: "02",
              tag: { da: "Konstruktion & Montage", en: "Framing & Build" },
              title: { da: "Professionelt tømrerarbejde", en: "Professional carpentry" },
              description: {
                da: "Tømrerarbejde ved nybyggeri, ombygning og renovering med fokus på bærende konstruktioner, lofter og vægge.",
                en: "Carpentry work across new builds, alterations, and renovations with strict focus on framing, ceilings, and partition walls."
              },
              bullet: { da: "Præcision og dokumenteret håndværkskvalitet", en: "Precision and certified craft quality" }
            },
            {
              number: "03",
              tag: { da: "Finish & Akustik", en: "Finish & Acoustics" },
              title: { da: "Specialelementer & inventar", en: "Bespoke elements & joinery" },
              description: {
                da: "Montage af specialelementer, inventar, akustikpaneler og trælameller til kontorer, boliger og erhvervsarealer.",
                en: "Mounting of custom architectural elements, joinery, acoustic panels, and wood slats for modern interiors."
              },
              bullet: { da: "Skræddersyet finish og æstetisk integration", en: "Tailored finish and aesthetic synergy" }
            },
            {
              number: "04",
              tag: { da: "Styring & Logistik", en: "Oversight & Logistics" },
              title: { da: "Tværfaglig koordinering", en: "Multi-trade coordination" },
              description: {
                da: "Koordinering med byggeledelse, underentreprenører og øvrige fag, så arbejdsrækkefølgen altid fungerer optimalt.",
                en: "Coordination with site supervisors, subcontractors, and adjacent trades, optimizing sequencing."
              },
              bullet: { da: "Struktur, adgangsforhold og sikkerhed", en: "Structure, access, and jobsite safety" }
            }
          ]
        },
        {
          id: "planning-and-execution",
          type: "dual-cards",
          tag: { da: "03 / Samarbejdsmodel & Faser", en: "03 / Process & Collaboration" },
          title: {
            da: "Sådan får vi jeres byggeprojekt godt fra start",
            en: "How we get your construction project off to a strong start"
          },
          paragraphs: [
            {
              da: "Vores indsats bliver tilpasset den fase, som jeres byggeprojekt befinder sig i. Vi kan deltage tidligt, når udførelsen skal planlægges, eller træde ind på en aktiv byggeplads med en klart defineret opgave. I begge tilfælde lægger vi vægt på aftaler, ansvar og løbende dialog med de relevante parter.",
              en: "Our input adapts to the current stage of your building venture. We can step in early during planning or join an active construction site with a clearly defined package. In both cases, we emphasize firm agreements, accountability, and proactive dialogue."
            }
          ],
          cards: [
            {
              level: "h3",
              tag: { da: "Fase 01 · Før Opstart", en: "Phase 01 · Before Mobilization" },
              title: {
                da: "Før opstart skaber vi et fælles grundlag",
                en: "Establishing a shared foundation before kickoff"
              },
              intro: {
                da: "Jo tidligere opgaven bliver afklaret, desto bedre kan materialer, bemanding og rækkefølge planlægges. Vi gennemgår den tilgængelige projektbeskrivelse og taler med jer om kvalitet, tidsmæssige krav og praktiske forhold. Det skaber et fælles grundlag for tilbuddet og den videre udførelse.",
                en: "The earlier the project parameters are resolved, the better materials, crew staffing, and sequencing can be synchronized. We review the available documentation and align on quality requirements, deadlines, and logistics, forming an unambiguous foundation for the quotation and execution."
              },
              bullets: {
                da: [
                  "Afklaring af projektbeskrivelse og tegninger",
                  "Fastlæggelse af bemanding, materialer og tidsmæssige krav",
                  "Gennemgang af tolerancer og finish før opstart",
                  "Gennemskueligt tilbud baseret på det konkrete omfang"
                ],
                en: [
                  "Review of project briefs, plans, and drawings",
                  "Scheduling of staffing, materials, and timeline constraints",
                  "Alignment on quality standards and aesthetic tolerances",
                  "Transparent proposal calculated on actual project scope"
                ]
              }
            },
            {
              level: "h3",
              tag: { da: "Fase 02 · Under Udførelsen", en: "Phase 02 · During Execution" },
              title: {
                da: "Under udførelsen holder vi fokus på koordinering",
                en: "Maintaining focus on coordination during execution"
              },
              intro: {
                type: "rich",
                da: "Når arbejdet går i gang, koordinerer vi vores leverancer med byggeledelsen og de øvrige fag på pladsen. Vi arbejder med respekt for adgangsveje, sikkerhedskrav og den samlede tidsplan. Ved behov kan vores {link:byggepladsservice} understøtte den praktiske drift og struktur.",
                en: "Once site work commences, we synchronize our packages with construction managers and fellow trades. We operate with strict respect for access routes, safety protocols, and the overarching timeline. If needed, our {link:byggepladsservice} can reinforce practical site logistics."
              },
              bullets: {
                da: [
                  "Løbende afstemning med byggeledelse og sidestillede entrepriser",
                  "Respekt for adgangsveje, sikkerhed og den overordnede tidsplan",
                  "Hurtig håndtering af uforudsete forhold og snitflader",
                  "Tæt dialog så ændringer opfanges i tide"
                ],
                en: [
                  "Ongoing coordination with site management and parallel contractors",
                  "Respect for access pathways, safety rules, and primary master schedule",
                  "Rapid resolution of unforeseen obstacles and interface shifts",
                  "Continuous dialogue so revisions are managed proactively"
                ]
              }
            }
          ]
        },
        {
          id: "carpentry-excellence",
          type: "architectural-box",
          tag: { da: "04 / Faglighed & Præcision", en: "04 / Craftsmanship & Precision" },
          title: {
            da: "Mere end en traditionel tømrervirksomhed",
            en: "More than a traditional carpentry business"
          },
          paragraphs: [
            {
              type: "rich",
              da: "Som tømrervirksomhed udfører vi både konstruktivt arbejde og synlige løsninger, hvor finish og præcision har stor betydning. Det omfatter blandt andet trækonstruktioner, lofter, beklædninger, specialinventar, facader og montage. Se mere om vores professionelle {link:toemrerarbejde}.",
              en: "As a carpentry and building specialist, we execute structural framing alongside visible installations where finish and millimetric accuracy are critical. This encompasses timber frames, acoustic ceilings, claddings, bespoke joinery, facades, and precision mounting. Read more about our professional {link:toemrerarbejde}."
            },
            {
              da: "Kombinationen af praktisk håndværk og projektforståelse gør os relevante for erhvervskunder, bygherrer, arkitekter og professionelle samarbejdspartnere. I får en håndværker, som kan løse sin egen opgave og samtidig forstå, hvordan arbejdet påvirker andre fag, efterfølgende leverancer og det færdige resultat.",
              en: "The combination of hands-on craftsmanship and broad project awareness makes us a reliable asset for corporate clients, property developers, architects, and professional general contractors. You gain a contractor who excels at their own trade while fully anticipating how it impacts adjoining trades and subsequent deliverables."
            }
          ],
          badgesLabel: { da: "Kernekompetencer & tømrerfaglige leverancer", en: "Core competencies & carpentry deliverables" },
          badges: {
            da: [
              "Trækonstruktioner & spær",
              "Akustikpaneler & lofter",
              "Træbeklædninger & facader",
              "Specialinventar & snedkeri",
              "Montage af præfabrikerede elementer",
              "Tværfaglig snitfladekoordinering",
              "Gennemarbejdet kvalitetssikring"
            ],
            en: [
              "Timber framing & structural roof works",
              "Acoustic paneling & architectural ceilings",
              "Wood claddings & facade systems",
              "Bespoke joinery & reception desks",
              "Mounting of prefabricated architectural modules",
              "Cross-trade interface coordination",
              "Thorough QA documentation"
            ]
          }
        },
        {
          id: "geographic-scope",
          type: "nationwide-coverage",
          tag: { da: "05 / Geografisk Dækning", en: "05 / Geographic Coverage" },
          title: {
            da: "Leder I efter en håndværker i København eller på Sjælland?",
            en: "Looking for craftsmen in Copenhagen or across Zealand?"
          },
          paragraphs: [
            {
              type: "rich",
              da: "CPH Collective har base i København og løser opgaver i hovedstadsområdet, på Sjælland og i Nordsjælland. Vi kører også ud til byggeprojekter i resten af Danmark, når opgavens omfang og karakter kræver vores kompetencer. Geografien bliver altid afklaret som en del af den første projektdialog.",
              en: "CPH Collective is based in Copenhagen, undertaking assignments across the Capital Region, North Zealand, and all of Zealand. We also mobilize to construction projects across Denmark when the scale and character of the work demand our capabilities. Geography is always addressed in our initial project consultation."
            },
            {
              type: "rich",
              da: "Uanset om I søger hjælp til et kommende projekt eller har behov for ekstra faglig kapacitet på en aktiv byggeplads, sikrer vores faglige forankring ro og fremdrift. Se eksempler på tidligere udførte opgaver under {link:projekter}.",
              en: "Whether you seek support for an upcoming development or require extra professional capacity on an active site, our trade proficiency guarantees stability and progress. Browse examples of completed projects in our {link:projekter} portfolio."
            }
          ],
          badges: {
            da: [
              "København & Frederiksberg",
              "Nordsjælland",
              "Storkøbenhavn & Omegn",
              "Hele Sjælland",
              "Byggeprojekter i hele Danmark",
              "Erhvervs- & kontorbyggeri",
              "Boligbyggeri & renovering"
            ],
            en: [
              "Copenhagen & Frederiksberg",
              "North Zealand",
              "Greater Copenhagen",
              "All of Zealand",
              "Projects across Denmark",
              "Commercial & office construction",
              "Residential builds & renovations"
            ]
          }
        },
        {
          id: "quote-consultation",
          type: "callout",
          title: {
            da: "Få håndværker hjælp til jeres byggeprojekt",
            en: "Get professional trade support for your building project"
          },
          paragraphs: [
            {
              da: "Kontakt os gerne, hvis I søger hjælp til et kommende byggeprojekt eller mangler ekstra faglig kapacitet på en aktiv byggeplads. Vi tager en uforpligtende samtale om opgaven, tidsplanen og jeres forventninger. Herefter kan vi vurdere den rette rolle og udarbejde et tilbud.",
              en: "Reach out if you need assistance for an upcoming construction project or require additional trade capacity on an active jobsite. We welcome an initial discussion regarding your requirements, schedule, and expectations to determine the right role and formulate a transparent quote."
            },
            {
              type: "rich",
              da: "{link:kontakt-os} og fortæl om projektet, så vi kan vurdere opgavens omfang og næste skridt.",
              en: "{link:kontakt-os} and tell us about your project so we can evaluate the scope and determine next steps."
            }
          ]
        }
      ]
    },
    includes: {
      da: [
        "Faglig håndværker hjælp til byggeprojekter",
        "Tømrerarbejde ved nybyggeri, ombygning og renovering",
        "Montage af specialelementer, inventar og akustikløsninger",
        "Tæt koordinering med byggeledelse og øvrige fag",
        "Grundig gennemgang af projektmateriale og grænseflader",
        "Kvalitetssikring og ryddelig aflevering"
      ],
      en: [
        "Professional trade support for construction projects",
        "Carpentry for new builds, alterations, and renovations",
        "Installation of bespoke elements, joinery, and acoustics",
        "Close coordination with site managers and parallel trades",
        "Rigorous review of project specifications and interfaces",
        "Quality assurance and clean, orderly handover"
      ]
    },
    process: [
      {
        step: "01",
        title: { da: "Indledende Afklaring", en: "Initial Scoping" },
        text: {
          da: "Vi gennemgår projektmateriale, tidsplan og snitflader og taler om forventninger til kvalitet og udførelse.",
          en: "We examine project specifications, schedules, and interfaces to align expectations on quality and craft."
        }
      },
      {
        step: "02",
        title: { da: "Gennemskueligt Tilbud", en: "Clear Proposal" },
        text: {
          da: "Tilbuddet baseres på det konkrete omfang frem for en standardløsning, så pladsens vilkår er tænkt ind.",
          en: "The quotation reflects real project conditions and exact scope rather than generic estimates."
        }
      },
      {
        step: "03",
        title: { da: "Koordinering & Montage", en: "Execution & Coordination" },
        text: {
          da: "Vores håndværkere udfører arbejdet med respekt for tidsplanen, logistik og sidestillede entrepriser.",
          en: "Our craftsmen execute on site with strict adherence to schedules, logistics, and adjacent trades."
        }
      },
      {
        step: "04",
        title: { da: "Kvalitet & Aflevering", en: "Quality & Handover" },
        text: {
          da: "Løbende opfølgning, afsluttende gennemgang og dokumenteret aflevering af vores ansvarsområde.",
          en: "Continuous verification, final inspection, and documented handover of all completed packages."
        }
      }
    ],
    benefits: [
      {
        title: { da: "Forstår hele projektet", en: "Holistic Understanding" },
        text: {
          da: "Vi ser ud over vores egen fagleverance og forstår, hvordan arbejdet påvirker andre fag og tidsplanen.",
          en: "We look beyond our single trade, understanding how each task impacts subsequent contractors and milestones."
        }
      },
      {
        title: { da: "Klar kommunikation", en: "Clear Communication" },
        text: {
          da: "Aftaler, fremdrift og ændringer håndteres proaktivt med byggeledelse og samarbejdspartnere.",
          en: "Agreements, schedule progress, and adjustments are coordinated proactively with site management."
        }
      },
      {
        title: { da: "København & Sjælland", en: "Copenhagen & Zealand" },
        text: {
          da: "Lokal forankring i København med kapacitet til opgaver på Sjælland og i resten af Danmark.",
          en: "Solid base in Copenhagen with capacity to execute across Zealand and all regions in Denmark."
        }
      }
    ],
    idealFor: {
      da: "Bygherrer, entreprenører, arkitekter og erhvervskunder, der har brug for ansvarsbevidste håndværkere til planlagte byggerier eller som ekstra faglig kapacitet på en aktiv byggeplads.",
      en: "Developers, main contractors, architects, and corporate clients seeking accountable craftsmen for scheduled construction or supplemental trade capacity on an active site."
    },
    image: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-faguddannede-toemrere-haandvaerkere.jpg",
    secondaryImage: "https://assets.auxodev.dk/assets/503cf246-11be-4a10-9327-e937ebb53ecb/cph-collective-snedkerarbejde-specialdesignet-traeinterioer.jpg",
    faq: [
      {
        q: {
          da: "Hvilke håndværkere kan CPH Collective stille til rådighed for et byggeprojekt?",
          en: "Which trades can CPH Collective supply for a construction project?"
        },
        a: {
          da: "Vores egen kernefaglighed er tømrer- og snedkerarbejde, hvor vi råder over et fast team af faglærte svende med bred byggeteknisk ekspertise. Derudover har vi faste, mangeårige samarbejdsaftaler med autoriserede el-installatører, vvs-installatører, murere og malere. Vi kan derfor levere enten en dedikeret tømrerfaglig entreprise eller et komplet, tværfagligt håndværkerhold til jeres byggeprojekt. Alle håndværkere er udvalgt på baggrund af dokumenteret faglig stolthed, pålidelighed og høj arbejdsmoral.",
          en: "Our internal core trade is high-end carpentry and joinery, staffed by certified journeymen with deep structural expertise. Furthermore, we maintain long-term partnership agreements with licensed electricians, certified plumbers, master masons, and painters. Consequently, we can deliver either a focused carpentry package or a coordinated multidisciplinary trade team for your construction project. Every tradesperson is selected based on proven trade pride, punctuality, and an exemplary work ethic."
        }
      },
      {
        q: {
          da: "Kan I indgå som underentreprenør på en anden hovedentreprenørs byggeplads?",
          en: "Can you serve as a subcontractor on another general contractor's site?"
        },
        a: {
          da: "Ja, vi fungerer ofte som pålidelig fagentreprenør på tømrerentrepriser for andre hoved- eller totalentreprenører. Vi forstår vigtigheden af at respektere pladsens overordnede tidsplan, sikkerhedsregler og samarbejde loyalt med øvrige fag. Vores svende er vant til at arbejde struktureret ud fra udbudsmateriale og levere dokumenteret kvalitetssikring.",
          en: "Yes, we frequently operate as a reliable trade contractor executing carpentry and installation packages for general contractors. We deeply appreciate the necessity of honoring master milestone schedules, site safety protocols, and collaborating harmoniously with adjacent trades. Our crews are accustomed to working rigorously from tender specifications and delivering methodical quality assurance documentation."
        }
      },
      {
        q: {
          da: "Hvordan sikrer I, at håndværkerne overholder aftalte deadlines?",
          en: "How do you ensure your craftsmen adhere to agreed construction milestones?"
        },
        a: {
          da: "Vi bemander alle opgaver med det nødvendige antal svende og allokerer ekstra ressourcer, hvis uforudsete faktorer kræver det. Vores formand følger den daglige produktion tæt og afstemmer fremdriften mod den overordnede tidsplan. Skulle der opstå forsinkelser fra forudgående faggrupper, omrokerer vi hurtigt vores arbejdsgange for at indhente tiden. Troværdighed og overholdte aftaler er fundamentet for vores gode omdømme i branchen.",
          en: "We staff all assignments with adequate trade manpower and deploy reserve capacity immediately if project conditions necessitate acceleration. Our foreman monitors daily trade production closely, continuously reconciling progress against the contractual milestones. If preceding trades cause unexpected delays, we adapt our sequencing swiftly to recover schedule slippage. Reliability and honoring commitments are the foundation of our trusted reputation across the Danish building sector."
        }
      },
      {
        q: {
          da: "Hvilke certifikater, forsikringer og garantier stiller jeres håndværkere med?",
          en: "What insurance coverage and trade warranties do your craftsmen provide?"
        },
        a: {
          da: "CPH Collective er fuldt dækket af professionel erhvervs- og all-risk ansvarsforsikring, som beskytter byggeriet og tredjepart under hele forløbet. Vores svende har de nødvendige certifikater til arbejde i højden, stilladsopstilling, brandforanstaltninger og maskinbetjening. På installationer benytter vi udelukkende autoriserede partnere, der udsteder lovpligtige overensstemmelseserklæringer. Bygherre opnår desuden fuld reklamationsret og garanti i henhold til dansk byggepraksis (AB18/AB Forbruger).",
          en: "CPH Collective is fully protected by comprehensive commercial and all-risk liability insurance, securing the project and third parties throughout construction. Our craftsmen hold all necessary safety certifications for working at height, scaffolding assembly, hot works, and plant operation. For mechanical and electrical installations, we exclusively utilize authorized contractors who issue formal compliance certificates. Clients additionally benefit from statutory warranty protections pursuant to Danish building conventions (AB18/AB Consumer)."
        }
      },
      {
        q: {
          da: "Hvordan indhenter man et uforpligtende tilbud på håndværkere til et byggeprojekt?",
          en: "How do we request a non-binding quotation for trades on a building project?"
        },
        a: {
          da: "I kontakter os blot via telefon eller vores kontaktformular med en kort beskrivelse af projektet samt eventuelle tegninger eller udbudsmateriale. Vi gennemgår materialet grundigt og inviterer eventuelt til en besigtigelse på pladsen for at afklare konkrete detaljer. Derefter modtager I et gennemskueligt, specificeret tilbud med en fast tidsramme og klare vilkår.",
          en: "Simply contact us via phone or our digital contact form with a brief summary of your project scope alongside any available blueprints or tender documents. We review the material thoroughly and, if beneficial, arrange an on-site walkthrough to clarify exact specifications. You then receive a transparent, itemized quotation complete with defined timelines and precise contract terms."
        }
      }
    ]
  }
];

export const ABOUT_DATA = {
  whoWeAre: {
    title: {
      da: "Hvem vi er",
      en: "Who We Are"
    },
    text: {
      da: "Vi er en dansk bygge- og tømrervirksomhed med fokus på kvalitet, ansvarlighed og professionel projektstyring. Vores tilgang er baseret på klar kommunikation, praktiske løsninger og respekt for tidsplaner og budgetter.",
      en: "We are a Danish construction and carpentry company with a focus on quality, responsibility, and professional project management. Our approach is based on clear communication, practical solutions, and respect for timelines and budgets."
    }
  },
  values: [
    {
      title: {
        da: "Kvalitet",
        en: "Quality"
      },
      description: {
        da: "Solidt håndværk og gennemtænkte løsninger, der er designet til at holde.",
        en: "Solid craftsmanship and thorough solutions designed to stand the test of time."
      }
    },
    {
      title: {
        da: "Pålidelighed",
        en: "Reliability"
      },
      description: {
        da: "Vi overholder aftaler og deadlines. Ansvarlighed er kernen i vores forretning.",
        en: "We meet agreements and deadlines. Accountability is at the core of our operations."
      }
    },
    {
      title: {
        da: "Gennemsigtighed",
        en: "Transparency"
      },
      description: {
        da: "Klar og ærlig kommunikation gennem hele projektet uden skjulte overraskelser.",
        en: "Clear, honest communication throughout the project lifecycle, with no hidden surprises."
      }
    },
    {
      title: {
        da: "Sikkerhed",
        en: "Safety"
      },
      description: {
        da: "Uforpligtende fokus på arbejdsmiljøet, streng overholdelse af regler og ansvarlighed.",
        en: "Uncompromising focus on the work environment, strict compliance, and accountability."
      }
    }
  ],
  whyChooseUs: [
    {
      title: {
        da: "Én dedikeret kontaktperson",
        en: "One Dedicated Contact"
      },
      description: {
        da: "En enkelt, ansvarlig projektleder gennem hele projektets livscyklus.",
        en: "A single, accountable project manager throughout the entire project lifecycle."
      }
    },
    {
      title: {
        da: "Erfaring med komplekse projekter",
        en: "Complex Project Experience"
      },
      description: {
        da: "Dokumenteret erfaring med totalentrepriser og komplekse byggeopgaver.",
        en: "Proven experience with turnkey projects and complex construction assignments."
      }
    },
    {
      title: {
        da: "Professionelt samarbejde på pladsen",
        en: "Professional Site Collaboration"
      },
      description: {
        da: "En samarbejdsorienteret, professionel tilgang til at arbejde sammen med andre faggrupper på aktive pladser.",
        en: "A collaborative, professional approach to working alongside other trades on active sites."
      }
    },
    {
      title: {
        da: "Struktureret udførelse",
        en: "Structured Execution"
      },
      description: {
        da: "Høj kvalitet, ingeniørmæssig udførelse med stram styring af tidsplan og budget.",
        en: "High-quality, engineered execution with rigorous scheduling and budget management."
      }
    }
  ]
};

export const CONTACT_INFO = {
  phone: "+45 31 70 56 49",
  email: "info@cphcollective.dk",
  address: "Vimmelskaftet 41A, 2. th., 1161 København K",
  hours: {
    da: "Man - Fre: 07:00 - 17:00",
    en: "Mon - Fri: 07:00 - 17:00"
  },
  cvr: "DK 41298304"
};
